import Drawflow from "drawflow";
import { initImagePickerEvents } from "@js/survey/imagePicker.js";
import { structureQuestions } from "@js/survey/elements/fragen.js";
import { structureSingle } from "@js/survey/elements/einfach.js";
import { structureMulti } from "@js/survey/elements/mehrfach.js";
import { structureFreetext } from "@js/survey/elements/freitext.js";
import { structureInfo } from "@js/survey/elements/info.js";
import { structureScale } from "@js/survey/elements/skala.js";
import { structureSlider } from "@js/survey/elements/slider.js";
import { structureCondition } from "@js/survey/elements/weiche.js";
import { structureRedirect } from "@js/survey/elements/weiterleitung.js";
import { structureContact } from "@js/survey/elements/kontakt.js";
import { structureSave } from "@js/survey/elements/speichern.js";
import { notifySuccess, notifyError, notifyConfirm } from "@js/components/notify";
import { surveyTemplates } from "@js/survey/surveyTemplates.js";

document.addEventListener("DOMContentLoaded", () => {
  const drawflowEl = document.getElementById("drawflow");
  if (!drawflowEl) return;

  let editor = null;
  let initialized = false;
  let unsavedChanges = false;

  const DEFAULT_SURVEY_STYLE = {
    primaryColor: "#ea580c",
    secondaryColor: "#f97316",
    btnTextColor: "#ffffff",
    submitBtnColor: "#22c55e",
    submitBtnTextColor: "#ffffff",
    bgColor: "#f8fafc",
    cardBgColor: "#ffffff",
    textColor: "#1e293b",
    answerBgColor: "#ffffff",
    answerTextColor: "#1e293b",
    answerBorderColor: "#e2e8f0",
    inputBgColor: "#ffffff",
    inputTextColor: "#1e293b",
    inputBorderColor: "#e2e8f0",
    borderRadius: "16px",
    kioskTimeout: 0,
  };
  const SURVEY_STYLE_PRESETS = {
    white_orange: {
      primaryColor: "#ea580c",
      secondaryColor: "#f97316",
      btnTextColor: "#ffffff",
      bgColor: "#f8fafc",
      cardBgColor: "#ffffff",
      textColor: "#1e293b",
      answerBgColor: "#ffffff",
      answerTextColor: "#1e293b",
      answerBorderColor: "#e2e8f0",
      inputBgColor: "#ffffff",
      inputTextColor: "#1e293b",
      inputBorderColor: "#e2e8f0",
      borderRadius: "16px",
      kioskTimeout: 0,
    },
    dark_orange: {
      primaryColor: "#f97316",
      secondaryColor: "#ea580c",
      btnTextColor: "#ffffff",
      bgColor: "#0f172a",
      cardBgColor: "#1e293b",
      textColor: "#f8fafc",
      answerBgColor: "#262e3d",
      answerTextColor: "#f8fafc",
      answerBorderColor: "#334155",
      inputBgColor: "#262e3d",
      inputTextColor: "#f8fafc",
      inputBorderColor: "#334155",
      borderRadius: "16px",
      kioskTimeout: 0,
    },
    light_blue: {
      primaryColor: "#2563eb",
      secondaryColor: "#3b82f6",
      btnTextColor: "#ffffff",
      bgColor: "#f0f9ff",
      cardBgColor: "#ffffff",
      textColor: "#0f172a",
      answerBgColor: "#ffffff",
      answerTextColor: "#0f172a",
      answerBorderColor: "#e2e8f0",
      inputBgColor: "#ffffff",
      inputTextColor: "#0f172a",
      inputBorderColor: "#e2e8f0",
      borderRadius: "16px",
      kioskTimeout: 0,
    },
  };

  let currentSurveyStyle = { ...DEFAULT_SURVEY_STYLE };

  // Lazy init: Drawflow needs a visible container to measure correctly
  const surveyTabLink = document.querySelector('a[data-bs-target="#survey"]');
  if (surveyTabLink) {
    surveyTabLink.addEventListener("shown.bs.tab", () => {
      if (!initialized) {
        initDrawflow();
        initialized = true;
      }
    });

    if (window.location.hash === "#survey" && !initialized) {
      initDrawflow();
      initialized = true;
    }
  }

  function initDrawflow() {
    const DrawflowConstructor =
      Drawflow && Drawflow.default ? Drawflow.default : Drawflow || window.Drawflow;
    if (!DrawflowConstructor) {
      console.error("Drawflow constructor not found.");
      return;
    }
    window.Drawflow = DrawflowConstructor;
    editor = new DrawflowConstructor(drawflowEl);
    editor.reroute = true;
    editor.reroute_fix_curvature = true;
    editor.force_first_input = false;
    editor.zoom_min = 0.2;
    editor.zoom_max = 2.0;
    editor.zoom_value = 0.1;
    editor.start();

    initImagePickerEvents(drawflowEl, editor);
    setupZoomControls();
    setupFullscreen();
    setupStyleModal();
    setupUndoRedoControls();
    setupTemplateLoader();
    setupDragAndDrop();
    setupNodeEvents();
    setupSaveButton();
    setupLivePreview();
    setupUnloadWarning();
    importExistingStructure();
  }

  // --- Zoom & Fit Controls ---

  function updateZoomLabel() {
    if (editor) {
      const pct = Math.round((editor.zoom || 1) * 100);
      const zoomResetBtn = document.getElementById("survey-zoom-reset");
      if (zoomResetBtn) zoomResetBtn.textContent = `${pct}%`;
    }
  }

  function fitToScreen() {
    if (!editor) return;
    const nodes = editor.drawflow?.drawflow?.Home?.data || {};
    const nodeKeys = Object.keys(nodes);
    if (!nodeKeys.length) {
      editor.zoom_reset();
      editor.canvas_x = 0;
      editor.canvas_y = 0;
      editor.zoom_refresh();
      updateZoomLabel();
      return;
    }

    let minX = Infinity,
      maxX = -Infinity,
      minY = Infinity,
      maxY = -Infinity;
    nodeKeys.forEach((k) => {
      const n = nodes[k];
      const posX = n.pos_x || 0;
      const posY = n.pos_y || 0;
      if (posX < minX) minX = posX;
      if (posX + 500 > maxX) maxX = posX + 500;
      if (posY < minY) minY = posY;
      if (posY + 350 > maxY) maxY = posY + 350;
    });

    const containerW = drawflowEl.clientWidth || 800;
    const containerH = drawflowEl.clientHeight || 600;
    const padding = 60;

    const contentW = maxX - minX + padding * 2;
    const contentH = maxY - minY + padding * 2;

    const scaleX = containerW / contentW;
    const scaleY = containerH / contentH;
    let targetZoom = Math.min(scaleX, scaleY, 1.0);
    targetZoom = Math.max(0.25, Math.min(targetZoom, 1.2));

    editor.zoom = targetZoom;
    const midX = (minX + maxX) / 2;
    const midY = (minY + maxY) / 2;

    editor.canvas_x = containerW / 2 - midX * targetZoom;
    editor.canvas_y = containerH / 2 - midY * targetZoom;

    editor.zoom_refresh();
    updateZoomLabel();
  }

  function setupZoomControls() {
    const zoomInBtn = document.getElementById("survey-zoom-in");
    const zoomOutBtn = document.getElementById("survey-zoom-out");
    const zoomResetBtn = document.getElementById("survey-zoom-reset");
    const zoomFitBtn = document.getElementById("survey-zoom-fit");

    if (zoomInBtn) {
      zoomInBtn.addEventListener("click", () => {
        editor.zoom_in();
        updateZoomLabel();
      });
    }

    if (zoomOutBtn) {
      zoomOutBtn.addEventListener("click", () => {
        editor.zoom_out();
        updateZoomLabel();
      });
    }

    if (zoomResetBtn) {
      zoomResetBtn.addEventListener("click", () => {
        editor.zoom_reset();
        updateZoomLabel();
      });
    }

    if (zoomFitBtn) {
      zoomFitBtn.addEventListener("click", () => {
        fitToScreen();
      });
    }

    drawflowEl.addEventListener("wheel", () => {
      setTimeout(updateZoomLabel, 50);
    });
  }

  // --- Fullscreen Controls ---

  function setupFullscreen() {
    const fullscreenBtn = document.getElementById("survey-fullscreen-btn");
    const surveyWrapper = document.querySelector(".survey-wrapper");
    if (!surveyWrapper) return;

    function toggleFullscreen(enable) {
      const isFs =
        enable !== undefined ? enable : !surveyWrapper.classList.contains("is-fullscreen");
      surveyWrapper.classList.toggle("is-fullscreen", isFs);

      const icon = document.getElementById("survey-fullscreen-icon");
      const text = document.getElementById("survey-fullscreen-text");

      if (isFs) {
        if (icon) {
          icon.classList.remove("bi-arrows-fullscreen");
          icon.classList.add("bi-fullscreen-exit");
        }
        if (text) text.textContent = "Vollbild beenden";
        if (fullscreenBtn) {
          fullscreenBtn.title = "Vollbild beenden (Esc)";
          fullscreenBtn.classList.remove("btn-outline-secondary");
          fullscreenBtn.classList.add("btn-outline-danger");
        }
        document.body.style.overflow = "hidden";
      } else {
        if (icon) {
          icon.classList.remove("bi-fullscreen-exit");
          icon.classList.add("bi-arrows-fullscreen");
        }
        if (text) text.textContent = "Vollbild";
        if (fullscreenBtn) {
          fullscreenBtn.title = "Vollbildmodus umschalten (Esc zum Beenden)";
          fullscreenBtn.classList.remove("btn-outline-danger");
          fullscreenBtn.classList.add("btn-outline-secondary");
        }
        document.body.style.overflow = "";
      }

      setTimeout(() => {
        if (editor) {
          editor.zoom_refresh();
          fitToScreen();
        }
      }, 100);
    }

    if (fullscreenBtn) {
      fullscreenBtn.addEventListener("click", () => toggleFullscreen());
    }

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && surveyWrapper.classList.contains("is-fullscreen")) {
        toggleFullscreen(false);
      }
    });
  }

  // --- Design & Style Controls ---

  function isDarkColor(hex) {
    if (!hex || typeof hex !== "string") return false;
    let c = hex.replace("#", "").trim();
    if (c.length === 3)
      c = c
        .split("")
        .map((x) => x + x)
        .join("");
    const num = parseInt(c, 16);
    if (isNaN(num)) return false;
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness < 140;
  }

  function updateStyleModalPreview(style) {
    const s = style || currentSurveyStyle;
    const container = document.getElementById("survey-style-preview-container");
    const card = document.getElementById("survey-style-preview-card");
    const btn = document.getElementById("survey-style-preview-btn");
    const heading = document.getElementById("survey-style-preview-heading");
    const text = document.getElementById("survey-style-preview-text");

    const isDark = isDarkColor(s.cardBgColor || s.bgColor);
    const subtextColor = isDark ? "#94a3b8" : "#64748b";
    const borderColor = isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.1)";
    const itemBg = isDark ? "rgba(255, 255, 255, 0.05)" : "#ffffff";

    if (container) container.style.setProperty("background", s.bgColor, "important");
    if (card) {
      card.style.setProperty("background", s.cardBgColor, "important");
      card.style.setProperty("border-radius", s.borderRadius, "important");
      card.style.setProperty("border-color", borderColor, "important");
    }
    if (heading) heading.style.setProperty("color", s.textColor, "important");
    if (text) text.style.setProperty("color", subtextColor, "important");

    const previewChoice =
      document.getElementById("survey-style-preview-choice") || card?.querySelector(".border.px-2");
    if (previewChoice) {
      previewChoice.style.setProperty("background", s.answerBgColor || itemBg, "important");
      previewChoice.style.setProperty("background-color", s.answerBgColor || itemBg, "important");
      previewChoice.style.setProperty(
        "border-color",
        s.answerBorderColor || borderColor,
        "important",
      );
      previewChoice.style.setProperty("color", s.answerTextColor || s.textColor, "important");
      previewChoice.style.setProperty(
        "border-radius",
        s.borderRadius === "0px" ? "0px" : "8px",
        "important",
      );
    }
    const previewBackBtn = card?.querySelector(".btn-outline-secondary");
    if (previewBackBtn) {
      previewBackBtn.style.setProperty("background", itemBg, "important");
      previewBackBtn.style.setProperty("border-color", borderColor, "important");
      previewBackBtn.style.setProperty("color", s.textColor, "important");
    }

    const previewInput = document.getElementById("survey-style-preview-input");
    if (previewInput) {
      const inputBg = s.inputBgColor || (isDark ? "#262e3d" : "#ffffff");
      const inputTextColor = s.inputTextColor || s.textColor;
      const inputBorderColor = s.inputBorderColor || (isDark ? "#334155" : "#e2e8f0");
      previewInput.style.setProperty("background", inputBg, "important");
      previewInput.style.setProperty("background-color", inputBg, "important");
      previewInput.style.setProperty("color", inputTextColor, "important");
      previewInput.style.setProperty("border-color", inputBorderColor, "important");
      previewInput.style.setProperty(
        "border-radius",
        s.borderRadius === "0px" ? "0px" : "8px",
        "important",
      );
    }

    if (btn) {
      btn.style.setProperty(
        "background",
        `linear-gradient(135deg, ${s.primaryColor} 0%, ${s.secondaryColor || s.primaryColor} 100%)`,
        "important",
      );
      btn.style.setProperty("color", s.btnTextColor || "#ffffff", "important");
      btn.style.setProperty("border", "none", "important");
      btn.style.setProperty(
        "border-radius",
        s.borderRadius === "0px" ? "0px" : s.borderRadius,
        "important",
      );
    }
  }

  function getFormStyle() {
    const kioskEnable = document.getElementById("survey-kiosk-enable")?.checked;
    const kioskTimeoutVal = parseInt(
      document.getElementById("survey-kiosk-timeout")?.value || "45",
      10,
    );

    return {
      primaryColor:
        document.getElementById("survey-style-primary")?.value || DEFAULT_SURVEY_STYLE.primaryColor,
      secondaryColor:
        document.getElementById("survey-style-secondary")?.value ||
        DEFAULT_SURVEY_STYLE.secondaryColor,
      btnTextColor:
        document.getElementById("survey-style-btn-text")?.value ||
        DEFAULT_SURVEY_STYLE.btnTextColor,
      submitBtnColor:
        document.getElementById("survey-style-submit-bg")?.value ||
        DEFAULT_SURVEY_STYLE.submitBtnColor,
      submitBtnTextColor:
        document.getElementById("survey-style-submit-text")?.value ||
        DEFAULT_SURVEY_STYLE.submitBtnTextColor,
      bgColor: document.getElementById("survey-style-bg")?.value || DEFAULT_SURVEY_STYLE.bgColor,
      cardBgColor:
        document.getElementById("survey-style-card-bg")?.value || DEFAULT_SURVEY_STYLE.cardBgColor,
      textColor:
        document.getElementById("survey-style-text")?.value || DEFAULT_SURVEY_STYLE.textColor,
      answerBgColor:
        document.getElementById("survey-style-answer-bg")?.value ||
        DEFAULT_SURVEY_STYLE.answerBgColor,
      answerTextColor:
        document.getElementById("survey-style-answer-text")?.value ||
        DEFAULT_SURVEY_STYLE.answerTextColor,
      answerBorderColor:
        document.getElementById("survey-style-answer-border")?.value ||
        DEFAULT_SURVEY_STYLE.answerBorderColor,
      inputBgColor:
        document.getElementById("survey-style-input-bg")?.value ||
        DEFAULT_SURVEY_STYLE.inputBgColor,
      inputTextColor:
        document.getElementById("survey-style-input-text")?.value ||
        DEFAULT_SURVEY_STYLE.inputTextColor,
      inputBorderColor:
        document.getElementById("survey-style-input-border")?.value ||
        DEFAULT_SURVEY_STYLE.inputBorderColor,
      borderRadius:
        document.getElementById("survey-style-radius")?.value || DEFAULT_SURVEY_STYLE.borderRadius,
      kioskTimeout: kioskEnable ? kioskTimeoutVal : 0,
    };
  }

  function populateStyleModal(style) {
    const s = style || currentSurveyStyle;
    const isDark = isDarkColor(s.cardBgColor || s.bgColor);
    const setVal = (id, val) => {
      const el = document.getElementById(id);
      if (el) el.value = val;
    };
    setVal("survey-style-primary", s.primaryColor);
    setVal("survey-style-primary-color", s.primaryColor);
    setVal("survey-style-secondary", s.secondaryColor);
    setVal("survey-style-secondary-color", s.secondaryColor);
    setVal("survey-style-btn-text", s.btnTextColor);
    setVal("survey-style-btn-text-color", s.btnTextColor);
    setVal("survey-style-submit-bg", s.submitBtnColor);
    setVal("survey-style-submit-bg-color", s.submitBtnColor);
    setVal("survey-style-submit-text", s.submitBtnTextColor);
    setVal("survey-style-submit-text-color", s.submitBtnTextColor);
    setVal("survey-style-bg", s.bgColor);
    setVal("survey-style-bg-color", s.bgColor);
    setVal("survey-style-card-bg", s.cardBgColor);
    setVal("survey-style-card-bg-color", s.cardBgColor);
    setVal("survey-style-text", s.textColor);
    setVal("survey-style-text-color", s.textColor);
    setVal("survey-style-answer-bg", s.answerBgColor || (isDark ? "#262e3d" : "#ffffff"));
    setVal("survey-style-answer-bg-color", s.answerBgColor || (isDark ? "#262e3d" : "#ffffff"));
    setVal("survey-style-answer-text", s.answerTextColor || s.textColor);
    setVal("survey-style-answer-text-color", s.answerTextColor || s.textColor);
    setVal("survey-style-answer-border", s.answerBorderColor || (isDark ? "#334155" : "#e2e8f0"));
    setVal(
      "survey-style-answer-border-color",
      s.answerBorderColor || (isDark ? "#334155" : "#e2e8f0"),
    );
    setVal("survey-style-input-bg", s.inputBgColor || (isDark ? "#262e3d" : "#ffffff"));
    setVal("survey-style-input-bg-color", s.inputBgColor || (isDark ? "#262e3d" : "#ffffff"));
    setVal("survey-style-input-text", s.inputTextColor || s.textColor);
    setVal("survey-style-input-text-color", s.inputTextColor || s.textColor);
    setVal("survey-style-input-border", s.inputBorderColor || (isDark ? "#334155" : "#e2e8f0"));
    setVal(
      "survey-style-input-border-color",
      s.inputBorderColor || (isDark ? "#334155" : "#e2e8f0"),
    );
    setVal("survey-style-radius", s.borderRadius);

    const kioskEnable = document.getElementById("survey-kiosk-enable");
    const kioskOptions = document.getElementById("survey-kiosk-options");
    const kioskTimeout = document.getElementById("survey-kiosk-timeout");
    const timeoutNum = parseInt(s.kioskTimeout || 0, 10);
    if (kioskEnable) {
      kioskEnable.checked = timeoutNum > 0;
    }
    if (kioskOptions) {
      kioskOptions.style.display = timeoutNum > 0 ? "block" : "none";
    }
    if (kioskTimeout) {
      if (timeoutNum > 0) kioskTimeout.value = String(timeoutNum);
      else kioskTimeout.value = "45";
    }

    updateStyleModalPreview(s);
  }

  function setupStyleModal() {
    const modalEl = document.getElementById("survey-design-modal");
    if (!modalEl) return;

    function syncColorPair(textId, colorId) {
      const textInput = document.getElementById(textId);
      const colorInput = document.getElementById(colorId);
      if (!textInput || !colorInput) return;

      colorInput.addEventListener("input", () => {
        textInput.value = colorInput.value;
        updateStyleModalPreview(getFormStyle());
      });

      textInput.addEventListener("input", () => {
        if (/^#[0-9A-Fa-f]{6}$/.test(textInput.value)) {
          colorInput.value = textInput.value;
        }
        updateStyleModalPreview(getFormStyle());
      });
    }

    syncColorPair("survey-style-primary", "survey-style-primary-color");
    syncColorPair("survey-style-secondary", "survey-style-secondary-color");
    syncColorPair("survey-style-btn-text", "survey-style-btn-text-color");
    syncColorPair("survey-style-submit-bg", "survey-style-submit-bg-color");
    syncColorPair("survey-style-submit-text", "survey-style-submit-text-color");
    syncColorPair("survey-style-bg", "survey-style-bg-color");
    syncColorPair("survey-style-card-bg", "survey-style-card-bg-color");
    syncColorPair("survey-style-text", "survey-style-text-color");
    syncColorPair("survey-style-answer-bg", "survey-style-answer-bg-color");
    syncColorPair("survey-style-answer-text", "survey-style-answer-text-color");
    syncColorPair("survey-style-answer-border", "survey-style-answer-border-color");
    syncColorPair("survey-style-input-bg", "survey-style-input-bg-color");
    syncColorPair("survey-style-input-text", "survey-style-input-text-color");
    syncColorPair("survey-style-input-border", "survey-style-input-border-color");

    const radiusSelect = document.getElementById("survey-style-radius");
    if (radiusSelect) {
      radiusSelect.addEventListener("change", () => {
        updateStyleModalPreview(getFormStyle());
      });
    }

    const kioskEnable = document.getElementById("survey-kiosk-enable");
    const kioskOptions = document.getElementById("survey-kiosk-options");
    if (kioskEnable && kioskOptions) {
      kioskEnable.addEventListener("change", () => {
        kioskOptions.style.display = kioskEnable.checked ? "block" : "none";
      });
    }

    // Presets
    document.querySelectorAll(".survey-preset-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        const presetKey = btn.dataset.preset;
        if (SURVEY_STYLE_PRESETS[presetKey]) {
          populateStyleModal(SURVEY_STYLE_PRESETS[presetKey]);
        }
      });
    });

    // Reset button
    const resetBtn = document.getElementById("survey-style-reset-btn");
    if (resetBtn) {
      resetBtn.addEventListener("click", () => {
        populateStyleModal(DEFAULT_SURVEY_STYLE);
      });
    }

    // Apply button
    const applyBtn = document.getElementById("survey-style-apply-btn");
    if (applyBtn) {
      applyBtn.addEventListener("click", () => {
        currentSurveyStyle = getFormStyle();
        unsavedChanges = true;
        const notifyFn = window.cs?.notifySuccess || notifySuccess;
        notifyFn("Design-Einstellungen übernommen. Bitte Struktur speichern!", "Design");
      });
    }

    modalEl.addEventListener("show.bs.modal", () => {
      populateStyleModal(currentSurveyStyle);
    });
  }

  // --- Validation ---

  function validateSurveyStructure() {
    if (!editor) return { valid: true, warnings: [] };
    const nodes = editor.drawflow?.drawflow?.Home?.data || {};
    const nodeKeys = Object.keys(nodes);
    const badge = document.getElementById("survey-validation-badge");

    drawflowEl
      .querySelectorAll(".node-warning")
      .forEach((el) => el.classList.remove("node-warning"));

    if (!nodeKeys.length) {
      if (badge) badge.classList.add("d-none");
      return { valid: true, warnings: [] };
    }

    const warnings = [];

    nodeKeys.forEach((k) => {
      const node = nodes[k];
      const isTerminal = node.class === "save" || node.class === "link";
      const outputs = node.outputs || {};
      let totalOutConns = 0;
      Object.values(outputs).forEach((out) => {
        totalOutConns += (out.connections || []).length;
      });

      if (!isTerminal && totalOutConns === 0) {
        const nodeEl = drawflowEl.querySelector(`#node-${node.id}`);
        if (nodeEl) nodeEl.classList.add("node-warning");
        warnings.push(`Knoten #${node.id} (${node.name}) hat keine ausgehende Verbindung.`);
      }
    });

    if (badge) {
      badge.classList.remove("d-none");
      if (warnings.length > 0) {
        badge.className = "badge bg-warning-subtle text-warning border border-warning-subtle";
        badge.innerHTML = `<i class="bi bi-exclamation-triangle me-1"></i> ${warnings.length} Warnung(en)`;
      } else {
        badge.className = "badge bg-success-subtle text-success border border-success-subtle";
        badge.innerHTML = '<i class="bi bi-check-circle me-1"></i> Struktur vollständig';
      }
    }

    return { valid: warnings.length === 0, warnings };
  }

  // --- Live Preview ---

  function setupLivePreview() {
    const previewBtn = document.getElementById("survey-preview-btn");
    const previewModalEl = document.getElementById("survey-preview-modal");
    const previewIframe = document.getElementById("survey-preview-iframe");
    const frameContainer = document.getElementById("survey-preview-frame-container");
    const mobileBtn = document.getElementById("survey-preview-device-mobile");
    const tabletBtn = document.getElementById("survey-preview-device-tablet");
    const desktopBtn = document.getElementById("survey-preview-device-desktop");
    const restartBtn = document.getElementById("survey-preview-restart");
    const campaignName =
      document.getElementById("survey-campaign-name")?.value || "Umfrage-Vorschau";

    if (!previewBtn || !previewModalEl || !previewIframe) return;

    let bsPreviewModal = null;

    function buildPreviewHtml(surveyData) {
      const surveyDataWithStyle = { ...surveyData, style: currentSurveyStyle };
      const surveyJsonStr = JSON.stringify(surveyDataWithStyle).replace(/</g, "\\u003c");
      const s = currentSurveyStyle || DEFAULT_SURVEY_STYLE;
      const isDark = isDarkColor(s.cardBgColor || s.bgColor);
      const subtextColor = isDark ? "#94a3b8" : "#64748b";
      const borderColor = isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.1)";
      const itemBg = isDark ? "rgba(255, 255, 255, 0.05)" : "#ffffff";
      const inputBg = s.inputBgColor || (isDark ? "rgba(0, 0, 0, 0.25)" : "#ffffff");
      const inputTextColor = s.inputTextColor || s.textColor;
      const inputBorder = s.inputBorderColor || borderColor;
      const trackBg = isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(148, 163, 184, 0.25)";
      const badgeBg = isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(148, 163, 184, 0.15)";
      const answerBg = s.answerBgColor || itemBg;
      const answerText = s.answerTextColor || s.textColor;
      const answerBorder = s.answerBorderColor || borderColor;

      function normalizeMediaUrl(url) {
        if (!url || typeof url !== "string") return "";
        url = url.trim();
        if (!url) return "";
        if (
          url.startsWith("http://") ||
          url.startsWith("https://") ||
          url.startsWith("data:") ||
          url.startsWith("/")
        ) {
          return url;
        }
        return "https://media.cs-erfolg.de/" + url;
      }

      const rawLogo = document.getElementById("logo")?.value?.trim() || "";
      const logoUrl = normalizeMediaUrl(rawLogo);
      const logoHtml = logoUrl
        ? `<div class="survey-brand-header" id="survey-brand-header"><img src="${logoUrl}" alt="${campaignName}" class="survey-brand-logo"></div>`
        : "";

      const rawFooter =
        window.tinymce?.get("footer")?.getContent() ??
        document.getElementById("footer")?.value ??
        "";
      const footerHtml = rawFooter.trim()
        ? `<footer class="survey-footer" id="survey-footer">${rawFooter}</footer>`
        : "";

      return `<!doctype html>
<html lang="de">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${campaignName}</title>
  <link rel="stylesheet" href="/css/survey-view.css">
  <style>
    :root {
      --survey-primary: ${s.primaryColor} !important;
      --survey-primary-hover: ${s.primaryColor} !important;
      --survey-secondary: ${s.secondaryColor} !important;
      --survey-btn-text: ${s.btnTextColor} !important;
      --survey-bg: ${s.bgColor} !important;
      --survey-bg-gradient: ${s.bgColor} !important;
      --survey-card-bg: ${s.cardBgColor} !important;
      --survey-text: ${s.textColor} !important;
      --survey-subtext: ${subtextColor} !important;
      --survey-border: ${borderColor} !important;
      --survey-track-bg: ${trackBg} !important;
      --survey-badge-bg: ${badgeBg} !important;
      --survey-item-bg: ${itemBg} !important;
      --survey-input-bg: ${inputBg} !important;
      --survey-input-text: ${inputTextColor} !important;
      --survey-input-border: ${inputBorder} !important;
      --survey-answer-bg: ${answerBg} !important;
      --survey-answer-text: ${answerText} !important;
      --survey-answer-border: ${answerBorder} !important;
      --survey-radius: ${s.borderRadius} !important;
      --survey-btn-radius: ${s.borderRadius} !important;
    }
    html, body {
      background: ${s.bgColor} !important;
      background-color: ${s.bgColor} !important;
      color: ${s.textColor} !important;
      padding: 1.5rem 0.5rem;
      min-height: 100vh;
      width: 100%;
      box-sizing: border-box;
    }
    .survey-page {
      padding: 0.5rem;
      min-height: auto;
      width: 100%;
      box-sizing: border-box;
      position: relative;
    }
    .survey-brand-header {
      position: absolute;
      top: 1rem;
      left: 1.5rem;
      z-index: 20;
      display: flex;
      align-items: center;
    }
    .survey-brand-logo {
      max-height: 44px;
      max-width: 160px;
      width: auto;
      height: auto;
      object-fit: contain;
      filter: drop-shadow(0 2px 5px rgba(0, 0, 0, 0.08));
    }
    @media (max-width: 768px) {
      .survey-brand-header {
        position: static;
        margin-bottom: 1.25rem;
        justify-content: center;
        width: 100%;
      }
      .survey-brand-logo {
        max-height: 36px;
        max-width: 130px;
      }
    }
    #survey-container {
      width: 100%;
      max-width: 680px;
      box-sizing: border-box;
    }
    .survey-card {
      width: 100%;
      max-width: 680px;
      box-sizing: border-box;
      background: ${s.cardBgColor} !important;
      background-color: ${s.cardBgColor} !important;
      border: 1px solid ${borderColor} !important;
      border-radius: ${s.borderRadius} !important;
      color: ${s.textColor} !important;
    }
    .survey-question, h1, h2, h3, h4, h5, h6 {
      color: ${s.textColor} !important;
    }
    .survey-text, .text-muted, .survey-slider-labels, .survey-slider-min-lbl, .survey-slider-max-lbl, .survey-slider-prefix, .survey-footer {
      color: ${subtextColor} !important;
    }
    .survey-footer a {
      color: ${s.textColor || "inherit"} !important;
    }
    .survey-step-count {
      background: ${badgeBg} !important;
      color: ${subtextColor} !important;
    }
    .survey-progress-bar, .survey-slider-input {
      background: ${trackBg} !important;
    }
    .btn-primary, button.btn-primary, .btn.btn-primary, input[type="submit"].btn-primary {
      background: linear-gradient(135deg, ${s.primaryColor} 0%, ${s.secondaryColor || s.primaryColor} 100%) !important;
      background-color: ${s.primaryColor} !important;
      color: ${s.btnTextColor || "#ffffff"} !important;
      border: none !important;
      border-color: ${s.primaryColor} !important;
      border-radius: ${s.borderRadius} !important;
    }
    button.btn-submit.btn-primary {
              background: ${s.submitBtnColor || s.primaryColor} !important;
              color: ${s.submitBtnTextColor || s.btnTextColor || "#ffffff"} !important;
              border: none !important;
              border-color: ${s.submitBtnColor || s.primaryColor} !important;
              border-radius: ${s.borderRadius} !important;
            }
    .btn-primary:hover, .btn-primary:focus, button.btn-primary:hover {
      background: linear-gradient(135deg, ${s.primaryColor} 0%, ${s.secondaryColor || s.primaryColor} 100%) !important;
      filter: brightness(0.92) !important;
      color: ${s.btnTextColor || "#ffffff"} !important;
    }
    button.btn-submit.btn-primary:hover, button.btn-submit.btn-primary:focus {
              background: ${s.submitBtnColor || s.primaryColor} !important;
              filter: brightness(0.92) !important;
              color: ${s.submitBtnTextColor || s.btnTextColor || "#ffffff"} !important;
            }
    .btn-outline-primary {
      border-color: ${s.primaryColor} !important;
      color: ${s.primaryColor} !important;
      border-radius: ${s.borderRadius} !important;
    }
    .btn-outline-secondary {
      background: ${itemBg} !important;
      border-color: ${borderColor} !important;
      color: ${s.textColor} !important;
      border-radius: ${s.borderRadius} !important;
    }
    .survey-choice label, .survey-answer-btn {
      background: ${answerBg} !important;
      background-color: ${answerBg} !important;
      border-color: ${answerBorder} !important;
      color: ${answerText} !important;
      border-radius: ${s.borderRadius} !important;
    }
    .survey-choice label span, .survey-answer-btn span {
      color: ${answerText} !important;
    }
    .survey-choice:has(input:checked) label, .survey-choice.checked label {
      background: linear-gradient(135deg, ${s.primaryColor} 0%, ${s.secondaryColor || s.primaryColor} 100%) !important;
      color: ${s.btnTextColor || "#ffffff"} !important;
      border-color: transparent !important;
    }
    .survey-choice:has(input:checked) label span, .survey-choice.checked label span {
      color: ${s.btnTextColor || "#ffffff"} !important;
    }
    .survey-rating-btn:not(.survey-rating-star):not(.survey-rating-heart) {
      background: ${itemBg} !important;
      border-color: ${borderColor} !important;
      color: ${subtextColor} !important;
    }
    .survey-rating-star {
      color: #eab308 !important;
      background: ${itemBg} !important;
      border-color: ${borderColor} !important;
    }
    .survey-rating-star.selected, .survey-rating-star.hover-fill, .survey-rating-star:hover {
      color: #eab308 !important;
      border-color: #eab308 !important;
      background: rgba(234, 179, 8, 0.2) !important;
    }
    .survey-rating-heart {
      color: #ef4444 !important;
      background: ${itemBg} !important;
      border-color: ${borderColor} !important;
    }
    .survey-rating-heart.selected, .survey-rating-heart.hover-fill, .survey-rating-heart:hover {
      color: #ef4444 !important;
      border-color: #ef4444 !important;
      background: rgba(239, 68, 68, 0.2) !important;
    }
    .survey-rating-num {
      background: ${itemBg} !important;
      border-color: ${borderColor} !important;
      color: ${s.textColor} !important;
    }
    .form-control, .form-select, .survey-textarea, .conditions-accordion-body {
      background-color: ${inputBg} !important;
      border-color: ${inputBorder} !important;
      color: ${inputTextColor} !important;
    }
    .survey-split-media { border-color: ${borderColor} !important; }
    .form-label, .form-check-label {
      color: ${s.textColor} !important;
    }
    .survey-choice input:checked + span, .survey-choice label:has(input:checked) {
      border-color: ${s.primaryColor} !important;
    }
    .survey-progress-fill {
      background: linear-gradient(90deg, ${s.primaryColor} 0%, ${s.secondaryColor || s.primaryColor} 100%) !important;
    }
  </style>
</head>
<body>
  <div class="survey-page">
    ${logoHtml}
    <div id="survey-container"></div>
    ${footerHtml}
  </div>

  <div id="survey-data" data-survey='${surveyJsonStr}' style="display:none;"></div>
  <form id="survey-submit-form" action="#" method="POST" style="display:none;">
    <input type="hidden" name="answers" id="survey-answers-input">
  </form>

  <template id="tpl-info">
    <div class="survey-card">
      <div class="survey-img" style="display:none;"><img src="" alt=""></div>
      <div class="survey-text mb-4"></div>
      <div class="survey-actions">
        <button type="button" class="btn btn-outline-secondary btn-back" style="display:none;">Zurück</button>
        <button type="button" class="btn btn-primary px-4 btn-next">Weiter</button>
      </div>
    </div>
  </template>

  <template id="tpl-freitext">
    <div class="survey-card">
      <h3 class="survey-question"></h3>
      <div class="survey-img" style="display:none;"><img src="" alt=""></div>
      <textarea class="form-control survey-textarea" rows="4" placeholder="Deine Antwort…"></textarea>
      <div class="survey-actions">
        <button type="button" class="btn btn-outline-secondary btn-back" style="display:none;">Zurück</button>
        <button type="button" class="btn btn-primary px-4 btn-next">Weiter</button>
      </div>
    </div>
  </template>

  <template id="tpl-einfach">
    <div class="survey-card">
      <h3 class="survey-question"></h3>
      <div class="survey-img" style="display:none;"><img src="" alt=""></div>
      <div class="survey-choices mb-4"></div>
      <div class="survey-actions">
        <button type="button" class="btn btn-outline-secondary btn-back" style="display:none;">Zurück</button>
        <button type="button" class="btn btn-primary px-4 btn-next">Weiter</button>
      </div>
    </div>
  </template>

  <template id="tpl-mehrfach">
    <div class="survey-card">
      <h3 class="survey-question"></h3>
      <div class="survey-img" style="display:none;"><img src="" alt=""></div>
      <div class="survey-choices mb-4"></div>
      <p class="survey-hint text-muted">Mehrfachauswahl möglich</p>
      <div class="survey-actions">
        <button type="button" class="btn btn-outline-secondary btn-back" style="display:none;">Zurück</button>
        <button type="button" class="btn btn-primary px-4 btn-next">Weiter</button>
      </div>
    </div>
  </template>

  <template id="tpl-skala">
    <div class="survey-card">
      <h3 class="survey-question"></h3>
      <div class="survey-img" style="display:none;"><img src="" alt=""></div>
      <div class="survey-rating mb-2" data-type=""></div>
      <div class="survey-actions">
        <button type="button" class="btn btn-outline-secondary btn-back" style="display:none;">Zurück</button>
        <button type="button" class="btn btn-primary btn-next">Weiter</button>
      </div>
    </div>
  </template>

  <template id="tpl-slider">
    <div class="survey-card">
      <h3 class="survey-question"></h3>
      <div class="survey-img" style="display:none;"><img src="" alt=""></div>
      <div class="survey-slider-wrap my-4">
        <div class="survey-slider-badge-wrap text-center mb-3">
          <span class="survey-slider-badge">
            <span class="survey-slider-prefix"></span><span class="survey-slider-val">50</span><span class="survey-slider-unit"> €</span>
          </span>
        </div>
        <div class="survey-slider-track-wrap">
          <input type="range" class="form-range survey-slider-input" min="0" max="100" step="1" value="50">
          <div class="survey-slider-labels d-flex justify-content-between text-muted small mt-2">
            <span class="survey-slider-min-lbl">0</span>
            <span class="survey-slider-max-lbl">100</span>
          </div>
        </div>
      </div>
      <div class="survey-actions">
        <button type="button" class="btn btn-outline-secondary btn-back" style="display:none;">Zurück</button>
        <button type="button" class="btn btn-primary btn-next">Weiter</button>
      </div>
    </div>
  </template>

  <template id="tpl-frage">
    <div class="survey-card">
      <h3 class="survey-question"></h3>
      <div class="survey-img" style="display:none;"><img src="" alt=""></div>
      <div class="survey-answers mb-4"></div>
      <div class="survey-back-wrap" style="display:none;">
        <button type="button" class="btn btn-outline-secondary btn-back">Zurück</button>
      </div>
    </div>
  </template>

  <template id="tpl-kontakt">
    <div class="survey-card">
      <div class="survey-img" style="display:none;"><img src="" alt=""></div>
      <h3 class="survey-question" style="display:none;"></h3>
      <p class="text-muted mb-3 survey-description" style="display:none;"></p>
      <form class="kontakt-form" novalidate>
        <div class="row g-3 mb-3">
          <div class="col-6">
            <label class="form-label">Vorname *</label>
            <input type="text" class="form-control" name="vorname" required>
          </div>
          <div class="col-6">
            <label class="form-label">Nachname *</label>
            <input type="text" class="form-control" name="nachname" required>
          </div>
        </div>
        <div class="kontakt-extra-fields"></div>
        <div class="survey-actions">
          <button type="button" class="btn btn-outline-secondary btn-back" style="display:none;">Zurück</button>
          <button type="submit" class="btn btn-primary px-4 btn-submit">Absenden</button>
        </div>
      </form>
    </div>
  </template>

  <template id="tpl-choice-radio">
    <div class="survey-choice">
      <label>
        <input type="radio" name="einfach">
        <span></span>
      </label>
    </div>
  </template>

  <template id="tpl-choice-checkbox">
    <div class="survey-choice">
      <label>
        <input type="checkbox" name="mehrfach">
        <span></span>
      </label>
    </div>
  </template>

  <template id="tpl-answer-btn">
    <button type="button" class="survey-answer-btn">
      <img class="survey-answer-img" alt="" style="display:none;">
      <span></span>
    </button>
  </template>

  <script>
    (function () {
      "use strict";
      const raw = document.getElementById("survey-data").dataset.survey;
      const SURVEY_DATA = raw ? JSON.parse(raw) : {};
      const nodes = SURVEY_DATA?.drawflow?.Home?.data ?? {};
      let answers = {};
      let history = [];
      const container = document.getElementById("survey-container");

      function normalizeMediaUrl(url) {
        if (!url || typeof url !== "string") return "";
        url = url.trim();
        if (!url) return "";
        if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:") || url.startsWith("/")) {
          return url;
        }
        return "https://media.cs-erfolg.de/" + url;
      }

      function cloneTemplate(id) {
        return document.getElementById(id).content.cloneNode(true);
      }

      function fillImage(frag, src) {
        const wrap = frag.querySelector(".survey-img");
        if (!wrap) return;
        if (src && src.trim()) {
          wrap.querySelector("img").src = normalizeMediaUrl(src);
          wrap.style.display = "";

          const card = frag.querySelector(".survey-card") || (frag.classList?.contains("survey-card") ? frag : null);
          if (card && !card.classList.contains("survey-card-split")) {
            card.classList.add("survey-card-split");

            const mediaCol = document.createElement("div");
            mediaCol.className = "survey-split-media";
            mediaCol.appendChild(wrap);

            const contentCol = document.createElement("div");
            contentCol.className = "survey-split-content";

            while (card.firstChild) {
              contentCol.appendChild(card.firstChild);
            }

            card.append(mediaCol, contentCol);
          }
        }
      }

      function showBackBtn(frag) {
        if (history.length > 0) {
          frag.querySelectorAll(".btn-back").forEach((b) => (b.style.display = ""));
        }
      }

      function getNextNode(node, outputIndex) {
        const conn = node.outputs?.["output_" + outputIndex]?.connections[0];
        return conn ? nodes[conn.node] : null;
      }

      function saveAnswer(node, value) {
        answers[node.id] = {
          type: node.class,
          frage:
            node.data.frage ||
            node.data.text ||
            node.data.header ||
            node.data.titel ||
            "",
          answer: value,
        };
      }

      function goNext(node, outputIndex, answerValue) {
        if (answerValue !== undefined && answerValue !== null) {
          saveAnswer(node, answerValue);
        }
        history.push(String(node.id));
        const next = getNextNode(node, outputIndex);
        if (next) {
          renderStep(next);
        } else {
          handleTerminal(node);
        }
      }

      function evaluateCondition(prevAnswer, operator, targetValue) {
        if (prevAnswer === undefined || prevAnswer === null) {
          return false;
        }

        if (Array.isArray(prevAnswer)) {
          if (operator === "contains" || operator === "eq") {
            return prevAnswer.some((item) =>
              String(item).toLowerCase().includes(String(targetValue).toLowerCase())
            );
          }
          if (operator === "neq") {
            return !prevAnswer.some(
              (item) => String(item).toLowerCase() === String(targetValue).toLowerCase()
            );
          }
          const numTarget = parseFloat(targetValue);
          if (!isNaN(numTarget)) {
            const len = prevAnswer.length;
            if (operator === "gte") return len >= numTarget;
            if (operator === "gt") return len > numTarget;
            if (operator === "lte") return len <= numTarget;
            if (operator === "lt") return len < numTarget;
          }
          return false;
        }

        const strAnswer = String(prevAnswer).trim();
        const strTarget = String(targetValue ?? "").trim();

        const numAnswer = parseFloat(strAnswer.replace(/,/g, "."));
        const numTarget = parseFloat(strTarget.replace(/,/g, "."));

        const isNumeric =
          !isNaN(numAnswer) && !isNaN(numTarget) && strAnswer !== "" && strTarget !== "";

        if (isNumeric) {
          switch (operator) {
            case "gte": return numAnswer >= numTarget;
            case "gt": return numAnswer > numTarget;
            case "lte": return numAnswer <= numTarget;
            case "lt": return numAnswer < numTarget;
            case "eq": return numAnswer === numTarget;
            case "neq": return numAnswer !== numTarget;
            case "contains": return strAnswer.toLowerCase().includes(strTarget.toLowerCase());
            default: return numAnswer >= numTarget;
          }
        }

        const lowerAnswer = strAnswer.toLowerCase();
        const lowerTarget = strTarget.toLowerCase();

        switch (operator) {
          case "eq": return lowerAnswer === lowerTarget;
          case "neq": return lowerAnswer !== lowerTarget;
          case "contains": return lowerAnswer.includes(lowerTarget);
          case "gte": return lowerAnswer >= lowerTarget;
          case "gt": return lowerAnswer > lowerTarget;
          case "lte": return lowerAnswer <= lowerTarget;
          case "lt": return lowerAnswer < lowerTarget;
          default: return lowerAnswer === lowerTarget;
        }
      }

      function goBack() {
        if (!history.length) return;
        let prevId = history.pop();
        while (
          prevId &&
          nodes[prevId] &&
          (nodes[prevId].class === "weiche" || nodes[prevId].class === "condition")
        ) {
          if (!history.length) return;
          prevId = history.pop();
        }
        if (prevId && nodes[prevId]) {
          renderStep(nodes[prevId]);
        }
      }

      function handleTerminal(node) {
        if (node.class === "link") {
          const url = node.data.url || "";
          window.open(url.startsWith("http") ? url : "https://" + url, "_blank");
          return;
        }
        showThankYou(node);
      }

      function resetToFirstStep() {
        answers = {};
        history = [];
        if (entry) {
          renderStep(entry);
        } else {
          location.reload();
        }
      }

      function showThankYou(node) {
        const card = document.createElement("div");
        card.className = "survey-card text-center";
        const d = node?.data || {};

        const content = document.createElement("div");
        content.className = "survey-split-content";

        const icon = document.createElement("div");
        icon.className = "survey-thanks-icon";
        icon.textContent = "✓";
        const h2 = document.createElement("h2");
        h2.className = "fw-bold mb-2";
        h2.textContent = d.titel || "Vielen Dank!";
        const p = document.createElement("p");
        p.className = "text-muted mb-4 fs-6";
        p.textContent = d.text || "Deine Antworten wurden erfolgreich erfasst (Vorschau-Modus).";
        content.append(icon, h2, p);

        const autoReloadSetting = String(
          node?.data?.autoreload ?? node?.data?.autoReload ?? ""
        ).toLowerCase();
        if (autoReloadSetting === "true" || autoReloadSetting === "1") {
          const progressBar = document.createElement("div");
          progressBar.className = "kiosk-progress-bar";
          const progressFill = document.createElement("div");
          progressFill.className = "kiosk-progress-fill";
          progressBar.appendChild(progressFill);
          content.appendChild(progressBar);

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              progressFill.style.width = "100%";
            });
          });

          setTimeout(() => {
            resetToFirstStep();
          }, 2000);
        } else if (
          autoReloadSetting !== "none" &&
          autoReloadSetting !== "no" &&
          autoReloadSetting !== "static"
        ) {
          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = "btn btn-outline-primary mt-2";
          btn.innerHTML = '<i class="bi bi-arrow-counterclockwise me-1"></i> Vorschau neu starten';
          btn.addEventListener("click", () => {
            resetToFirstStep();
          });
          content.appendChild(btn);
        }

        if (d.frageimg && d.frageimg.trim()) {
          card.classList.add("survey-card-split");
          const mediaCol = document.createElement("div");
          mediaCol.className = "survey-split-media";
          const imgWrap = document.createElement("div");
          imgWrap.className = "survey-img";
          const img = document.createElement("img");
          img.src = d.frageimg.trim();
          img.alt = "";
          imgWrap.appendChild(img);
          mediaCol.appendChild(imgWrap);
          card.append(mediaCol, content);
          container.classList.add("has-split-card");
        } else {
          card.append(...content.childNodes);
          container.classList.remove("has-split-card");
        }

        container.replaceChildren(card);
      }

      // Kiosk Inaktivitäts-Timer
      const kioskTimeoutSeconds = parseInt(SURVEY_DATA?.style?.kioskTimeout || 0, 10);
      let idleTimer = null;
      let countdownTimer = null;
      let countdownSeconds = 5;

      function removeIdleWarning() {
        if (countdownTimer) {
          clearInterval(countdownTimer);
          countdownTimer = null;
        }
        const warn = document.querySelector(".kiosk-idle-warning");
        if (warn) warn.remove();
      }

      function showIdleWarning() {
        removeIdleWarning();
        if (history.length === 0 && Object.keys(answers).length === 0) {
          return;
        }
        countdownSeconds = 5;
        const warnEl = document.createElement("div");
        warnEl.className = "kiosk-idle-warning";
        warnEl.innerHTML = \`
          <i class="bi bi-hourglass-split text-warning"></i>
          <span>Inaktiv: Zurücksetzen in <span class="kiosk-countdown-badge">\${countdownSeconds}</span>s</span>
          <button type="button" class="kiosk-stay-btn">Weiter ausfüllen</button>
        \`;
        warnEl.querySelector(".kiosk-stay-btn").addEventListener("click", (e) => {
          e.stopPropagation();
          resetIdleTimer();
        });
        document.body.appendChild(warnEl);

        countdownTimer = setInterval(() => {
          countdownSeconds--;
          const badge = warnEl.querySelector(".kiosk-countdown-badge");
          if (badge) badge.textContent = countdownSeconds;
          if (countdownSeconds <= 0) {
            removeIdleWarning();
            resetToFirstStep();
          }
        }, 1000);
      }

      function resetIdleTimer() {
        removeIdleWarning();
        if (idleTimer) clearTimeout(idleTimer);
        if (kioskTimeoutSeconds > 0) {
          const warnDelay = Math.max(1, kioskTimeoutSeconds - 5) * 1000;
          idleTimer = setTimeout(() => {
            if (kioskTimeoutSeconds <= 5) {
              resetToFirstStep();
            } else {
              showIdleWarning();
            }
          }, warnDelay);
        }
      }

      if (kioskTimeoutSeconds > 0) {
        ["touchstart", "pointerdown", "mousedown", "keydown", "scroll", "input", "click"].forEach((evt) => {
          window.addEventListener(evt, resetIdleTimer, { passive: true });
        });
        resetIdleTimer();
      }

      function renderInfo(node) {
        const frag = cloneTemplate("tpl-info");
        frag.querySelector(".survey-text").textContent = node.data.text || "";
        fillImage(frag, node.data.frageimg);
        showBackBtn(frag);
        return frag;
      }

      function renderFreitext(node) {
        const frag = cloneTemplate("tpl-freitext");
        frag.querySelector(".survey-question").textContent = node.data.frage || "";
        fillImage(frag, node.data.frageimg);
        showBackBtn(frag);
        return frag;
      }

      function renderEinfach(node) {
        const frag = cloneTemplate("tpl-einfach");
        frag.querySelector(".survey-question").textContent = node.data.frage || "";
        fillImage(frag, node.data.frageimg);
        showBackBtn(frag);
        const choicesEl = frag.querySelector(".survey-choices");
        [1, 2, 3, 4, 5, 6]
          .map((i) => node.data["answer" + i])
          .filter((a) => a && a.trim())
          .forEach((a) => {
            const item = cloneTemplate("tpl-choice-radio");
            item.querySelector("input").value = a;
            item.querySelector("span").textContent = a;
            choicesEl.appendChild(item);
          });
        return frag;
      }

      function renderMehrfach(node) {
        const frag = cloneTemplate("tpl-mehrfach");
        frag.querySelector(".survey-question").textContent = node.data.frage || "";
        fillImage(frag, node.data.frageimg);
        showBackBtn(frag);
        const choicesEl = frag.querySelector(".survey-choices");
        [1, 2, 3, 4, 5, 6]
          .map((i) => node.data["answer" + i])
          .filter((a) => a && a.trim())
          .forEach((a) => {
            const item = cloneTemplate("tpl-choice-checkbox");
            item.querySelector("input").value = a;
            item.querySelector("span").textContent = a;
            choicesEl.appendChild(item);
          });
        return frag;
      }

      function normalizeScaleType(rawType) {
        const t = (rawType || "").toLowerCase().trim();
        if (t === "star" || t === "sterne" || t === "stars") return "star";
        if (t === "heart" || t === "herzen" || t === "hearts") return "heart";
        if (t === "number" || t === "numbers" || t === "nummern" || t === "zahlen") return "number";
        if (t === "smiley3" || t === "3smileys" || t === "3-smileys") return "smiley3";
        if (t === "smiley" || t === "smileys") return "smiley";
        if (t === "smiley5" || t === "5smileys" || t === "5-smileys") return "smiley5";
        return "star";
      }

      function renderSkala(node) {
        const type = normalizeScaleType(node.data.type || node.data.skalaTyp);
        const count = parseInt(node.data.count || "5", 10);
        const frag = cloneTemplate("tpl-skala");
        frag.querySelector(".survey-question").textContent = node.data.frage || "";
        fillImage(frag, node.data.frageimg);
        showBackBtn(frag);
        const ratingEl = frag.querySelector(".survey-rating");
        ratingEl.dataset.type = type;

        const emojiMap = {
          smiley3: ["😞", "😐", "😊"],
          smiley5: ["😞", "😟", "😐", "🙂", "😊"],
          smiley: ["😞", "😊"],
        };

        const makeRatingBtn = (cls, value, text) => {
          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = "survey-rating-btn " + cls;
          btn.dataset.value = value;
          btn.textContent = text;
          return btn;
        };

        if (emojiMap[type]) {
          const total = emojiMap[type].length;
          emojiMap[type].forEach((e, i) => {
            const btn = makeRatingBtn("survey-rating-emoji", i + 1, e);
            btn.dataset.step = i + 1;
            btn.dataset.total = total;
            ratingEl.appendChild(btn);
          });
        } else if (type === "number") {
          Array.from({ length: count }, (_, i) => i + 1).forEach((n) =>
            ratingEl.appendChild(makeRatingBtn("survey-rating-num", n, n))
          );
        } else {
          const isHeart = type === "heart";
          const icon = isHeart ? "♥" : "★";
          const cls = isHeart ? "survey-rating-heart" : "survey-rating-star";
          Array.from({ length: count }, (_, i) => i + 1).forEach((n) =>
            ratingEl.appendChild(makeRatingBtn(cls, n, icon))
          );
        }
        return frag;
      }

      function renderSlider(node) {
        const d = node.data || {};
        const frag = cloneTemplate("tpl-slider");
        frag.querySelector(".survey-question").textContent = d.frage || "";
        fillImage(frag, d.frageimg);
        showBackBtn(frag);

        const min = parseFloat(d.min || 0);
        const max = parseFloat(d.max || 100);
        const step = parseFloat(d.step || 1);
        const defaultVal = parseFloat(
          d.default !== undefined && d.default !== "" ? d.default : (min + max) / 2
        );
        const unit = d.unit !== undefined ? d.unit : "€";
        const prefix = d.prefix || "";

        const sliderInput = frag.querySelector(".survey-slider-input");
        const valEl = frag.querySelector(".survey-slider-val");
        const unitEl = frag.querySelector(".survey-slider-unit");
        const prefixEl = frag.querySelector(".survey-slider-prefix");
        const minLbl = frag.querySelector(".survey-slider-min-lbl");
        const maxLbl = frag.querySelector(".survey-slider-max-lbl");

        sliderInput.min = min;
        sliderInput.max = max;
        sliderInput.step = step;
        sliderInput.value = defaultVal;

        prefixEl.textContent = prefix;
        unitEl.textContent = unit ? (unit.startsWith(" ") ? unit : " " + unit) : "";
        valEl.textContent = defaultVal.toLocaleString("de-DE");
        minLbl.textContent = (prefix ? prefix : "") + min.toLocaleString("de-DE") + (unit ? " " + unit.trim() : "");
        maxLbl.textContent = (prefix ? prefix : "") + max.toLocaleString("de-DE") + (unit ? " " + unit.trim() : "");

        return frag;
      }

      function renderFrage(node) {
        const frag = cloneTemplate("tpl-frage");
        frag.querySelector(".survey-question").textContent = node.data.frage || "";
        fillImage(frag, node.data.frageimg);
        if (history.length > 0) {
          frag.querySelector(".survey-back-wrap").style.display = "";
        }
        const count = node.class === "frage2" ? 2 : node.class === "frage3" ? 3 : 4;
        const answersEl = frag.querySelector(".survey-answers");
        Array.from({ length: count }, (_, i) => i + 1).forEach((i) => {
          const item = cloneTemplate("tpl-answer-btn");
          item.querySelector("button").dataset.output = i;
          const imgSrc = node.data["answer" + i + "img"] || "";
          if (imgSrc) {
            const img = item.querySelector("img");
            img.src = imgSrc;
            img.style.display = "";
          }
          item.querySelector("span").textContent = node.data["answer" + i] || "";
          answersEl.appendChild(item);
        });
        return frag;
      }

      function renderKontakt(node) {
        const d = node.data;
        const frag = cloneTemplate("tpl-kontakt");
        fillImage(frag, d.frageimg);
        const header = d.header || d.titel;
        if (header) {
          const h = frag.querySelector(".survey-question");
          h.textContent = header;
          h.style.display = "";
        }
        const description = d.description || d.untertitel;
        if (description) {
          const p = frag.querySelector(".survey-description");
          p.textContent = description;
          p.style.display = "";
        }
        showBackBtn(frag);

        const extra = frag.querySelector(".kontakt-extra-fields");
        const makeField = (label, type, name, isRequired = false) => {
          const wrap = document.createElement("div");
          wrap.className = "mb-3";
          const lbl = document.createElement("label");
          lbl.className = "form-label";
          lbl.textContent = label;
          const inp = document.createElement("input");
          inp.type = type;
          inp.className = "form-control";
          inp.name = name;
          if (isRequired) inp.required = true;
          wrap.append(lbl, inp);
          return wrap;
        };

        const isMailActive =
          d.mail === "1" ||
          d.mail === "true" ||
          d.mail === true ||
          d.mail === "optional" ||
          d.mail === "2";
        const isMailRequired = d.mail === "1" || d.mail === "true" || d.mail === true;
        if (isMailActive) {
          extra.appendChild(
            makeField(
              isMailRequired ? "E-Mail *" : "E-Mail (optional)",
              "email",
              "email",
              isMailRequired,
            ),
          );
        }

        const isPhoneActive =
          d.phone === "1" ||
          d.phone === "true" ||
          d.phone === true ||
          d.phone === "optional" ||
          d.phone === "2";
        const isPhoneRequired = d.phone === "1" || d.phone === "true" || d.phone === true;
        if (isPhoneActive) {
          extra.appendChild(
            makeField(
              isPhoneRequired ? "Telefon *" : "Telefon (optional)",
              "tel",
              "telefon",
              isPhoneRequired,
            ),
          );
        }

        if (d.date === "1") extra.appendChild(makeField("Geburtsdatum", "date", "geburtsdatum"));

        if (d.address === "1") {
          extra.appendChild(makeField("Straße & Hausnummer", "text", "strasse"));
          const row = document.createElement("div");
          row.className = "row g-3 mb-3";
          const col1 = document.createElement("div");
          col1.className = "col-4";
          const col2 = document.createElement("div");
          col2.className = "col-8";
          col1.appendChild(makeField("PLZ", "text", "plz"));
          col2.appendChild(makeField("Ort", "text", "ort"));
          row.append(col1, col2);
          extra.appendChild(row);
        }

        if (d.member === "1") {
          const wrap = document.createElement("div");
          wrap.className = "mb-3";
          const lbl = document.createElement("label");
          lbl.className = "form-label";
          lbl.textContent = "Bereits Kunde?";
          const sel = document.createElement("select");
          sel.className = "form-select";
          sel.name = "mitglied";
          sel.innerHTML = '<option value="0">Nein</option><option value="1">Ja</option>';
          wrap.append(lbl, sel);
          extra.appendChild(wrap);
        }

        const isConditionsActive =
          d.conditions === "1" ||
          d.conditions === "optional" ||
          d.conditions === "true" ||
          d.conditions === true ||
          d.conditions === "2";
        const isConditionsRequired =
          d.conditions === "1" || d.conditions === "true" || d.conditions === true;

        if (isConditionsActive) {
          const wrap = document.createElement("div");
          wrap.className = "mb-3 survey-conditions-wrap";

          const row = document.createElement("div");
          row.className = "d-flex align-items-center justify-content-between flex-wrap gap-1";

          const formCheck = document.createElement("div");
          formCheck.className = "form-check mb-0";

          const cb = document.createElement("input");
          cb.type = "checkbox";
          cb.className = "form-check-input";
          cb.name = "conditions";
          cb.id = "survey-conditions-cb-preview";
          if (isConditionsRequired) cb.required = true;

          const lbl = document.createElement("label");
          lbl.className = "form-check-label";
          lbl.htmlFor = "survey-conditions-cb-preview";
          lbl.textContent = isConditionsRequired
            ? "Ich akzeptiere die Teilnahmebedingungen *"
            : "Ich akzeptiere die Teilnahmebedingungen (optional)";

          formCheck.append(cb, lbl);

          const toggleBtn = document.createElement("button");
          toggleBtn.type = "button";
          toggleBtn.className =
            "btn btn-link btn-sm text-decoration-none p-0 text-muted conditions-accordion-toggle";
          toggleBtn.style.fontSize = "0.825rem";
          toggleBtn.innerHTML = '<span>Details anzeigen</span> <i class="bi bi-chevron-down ms-1"></i>';

          row.append(formCheck, toggleBtn);

          const body = document.createElement("div");
          body.className = "conditions-accordion-body mt-2 p-3 text-muted";
          body.style.display = "none";
          body.style.fontSize = "0.825rem";
          body.style.lineHeight = "1.5";
          body.style.maxHeight = "180px";
          body.style.overflowY = "auto";
          body.style.whiteSpace = "pre-wrap";
          body.style.border = "2px solid var(--survey-border)";
          body.style.borderRadius = "var(--survey-radius, 12px)";
          body.style.backgroundColor = "var(--survey-input-bg, rgba(0, 0, 0, 0.02))";

          const defaultTerms =
            "Teilnahmebedingungen:\\n\\n1. Die Teilnahme an der Aktion ist freiwillig und kostenlos.\\n2. Teilnahmeberechtigt sind Personen ab 18 Jahren.\\n3. Die erhobenen Kontaktdaten werden vertraulich und ausschließlich im Rahmen dieser Aktion verarbeitet.\\n4. Eine Barauszahlung oder Übertragung auf Dritte ist ausgeschlossen.";

          body.textContent =
            d.conditionstext && d.conditionstext.trim()
              ? d.conditionstext.trim()
              : defaultTerms;

          toggleBtn.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            const isClosed = body.style.display === "none";
            body.style.display = isClosed ? "block" : "none";
            toggleBtn.innerHTML = isClosed
              ? '<span>Details ausblenden</span> <i class="bi bi-chevron-up ms-1"></i>'
              : '<span>Details anzeigen</span> <i class="bi bi-chevron-down ms-1"></i>';
          });

          wrap.append(row, body);
          extra.appendChild(wrap);
        }

        const isDatenschutzActive =
          d.datenschutz === "1" ||
          d.datenschutz === "optional" ||
          d.datenschutz === "true" ||
          d.datenschutz === true ||
          d.datenschutz === "2";
        const isDatenschutzRequired =
          d.datenschutz === "1" || d.datenschutz === "true" || d.datenschutz === true;

        if (isDatenschutzActive) {
          const wrap = document.createElement("div");
          wrap.className = "mb-3 form-check";
          const cb = document.createElement("input");
          cb.type = "checkbox";
          cb.className = "form-check-input";
          cb.name = "datenschutz";
          cb.id = "survey-datenschutz-cb-preview";
          if (isDatenschutzRequired) cb.required = true;

          const lbl = document.createElement("label");
          lbl.className = "form-check-label";
          lbl.htmlFor = "survey-datenschutz-cb-preview";

          const dsUrl = (d.datenschutzurl || "").trim();
          if (dsUrl) {
            const prefix = document.createTextNode("Ich akzeptiere die ");
            const link = document.createElement("a");
            link.href = dsUrl;
            link.target = "_blank";
            link.rel = "noopener noreferrer";
            link.className = "text-primary text-decoration-underline fw-semibold";
            link.textContent = "Datenschutzerklärung";
            link.addEventListener("click", (e) => e.stopPropagation());
            const suffix = document.createTextNode(isDatenschutzRequired ? " *" : " (optional)");
            lbl.append(prefix, link, suffix);
          } else {
            lbl.textContent = isDatenschutzRequired
              ? "Ich akzeptiere die Datenschutzerklärung *"
              : "Ich akzeptiere die Datenschutzerklärung (optional)";
          }

          wrap.append(cb, lbl);
          extra.appendChild(wrap);
        }

        if (d.button) frag.querySelector(".btn-submit").textContent = d.button;

        return frag;
      }

      function bindEvents(node) {
        function validate(node) {
          switch (node.class) {
            case "freitext": {
              const textarea = container.querySelector(".survey-textarea");
              if (!textarea || !textarea.value.trim()) {
                textarea?.classList.add("is-invalid");
                textarea?.focus();
                return "Bitte gib eine Antwort in das Textfeld ein.";
              }
              return null;
            }
            case "einfach": {
              const checked = container.querySelector('input[name="einfach"]:checked');
              return checked ? null : "Bitte wähle eine Option aus.";
            }
            case "mehrfach": {
              const checked = container.querySelectorAll('input[name="mehrfach"]:checked');
              return checked.length > 0 ? null : "Bitte wähle mindestens eine Option aus.";
            }
            case "skala": {
              const selected = container.querySelectorAll(".survey-rating-btn.selected");
              return selected.length > 0
                ? null
                : "Bitte wähle eine Bewertung aus (mindestens 1 Stern / Punkt).";
            }
            default:
              return null;
          }
        }

        function showInlineError(msg) {
          let err = container.querySelector(".survey-error");
          if (!err) {
            err = document.createElement("div");
            err.className = "survey-error";
            const actions = container.querySelector(".survey-actions");
            if (actions) actions.before(err);
            else container.appendChild(err);
          }
          err.textContent = msg;
        }

        function clearInlineError() {
          container.querySelector(".survey-error")?.remove();
        }

        container.querySelectorAll(".btn-back").forEach((b) => b.addEventListener("click", goBack));

        container.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((inp) => {
          inp.addEventListener("change", clearInlineError);
        });
        const textarea = container.querySelector(".survey-textarea");
        if (textarea) {
          textarea.addEventListener("input", () => {
            textarea.classList.remove("is-invalid");
            clearInlineError();
          });
        }

        const btnNext = container.querySelector(".btn-next");
        if (btnNext) {
          btnNext.addEventListener("click", () => {
            const error = validate(node);
            if (error) {
              showInlineError(error);
              return;
            }
            clearInlineError();

            switch (node.class) {
              case "info":
                goNext(node, 1, null);
                break;
              case "freitext":
                goNext(node, 1, container.querySelector(".survey-textarea").value.trim());
                break;
              case "einfach": {
                const checked = container.querySelector('input[name="einfach"]:checked');
                goNext(node, 1, checked.value);
                break;
              }
              case "mehrfach": {
                const checked = [...container.querySelectorAll('input[name="mehrfach"]:checked')].map((el) => el.value);
                goNext(node, 1, checked);
                break;
              }
              case "skala": {
                const selected = [...container.querySelectorAll(".survey-rating-btn.selected")];
                goNext(node, 1, selected[selected.length - 1].dataset.value);
                break;
              }
              case "slider": {
                const input = container.querySelector(".survey-slider-input");
                goNext(node, 1, input ? input.value : 0);
                break;
              }
            }
          });
        }

        container.querySelectorAll(".survey-answer-btn").forEach((btn) => {
          btn.addEventListener("click", () => {
            goNext(node, parseInt(btn.dataset.output), btn.querySelector("span").textContent.trim());
          });
        });

        const sliderWrap = container.querySelector(".survey-slider-wrap");
        if (sliderWrap) {
          const input = sliderWrap.querySelector(".survey-slider-input");
          const valEl = sliderWrap.querySelector(".survey-slider-val");
          const updateVal = () => {
            const num = parseFloat(input.value);
            if (valEl) valEl.textContent = isNaN(num) ? input.value : num.toLocaleString("de-DE");
          };
          input?.addEventListener("input", updateVal);
          input?.addEventListener("change", updateVal);
        }

        const ratingEl = container.querySelector(".survey-rating");
        if (ratingEl) {
          const isFillUp = ["star", "heart", "number"].includes(normalizeScaleType(ratingEl.dataset.type));
          const buttons = ratingEl.querySelectorAll(".survey-rating-btn");

          buttons.forEach((btn) => {
            if (isFillUp) {
              btn.addEventListener("mouseenter", () => {
                const val = parseInt(btn.dataset.value, 10);
                buttons.forEach((b) => {
                  b.classList.toggle("hover-fill", parseInt(b.dataset.value, 10) <= val);
                });
              });
              btn.addEventListener("mouseleave", () => {
                buttons.forEach((b) => b.classList.remove("hover-fill"));
              });
            }

            btn.addEventListener("click", () => {
              clearInlineError();
              const val = parseInt(btn.dataset.value, 10);
              buttons.forEach((b) => {
                b.classList.toggle("selected", isFillUp ? parseInt(b.dataset.value, 10) <= val : b === btn);
              });
            });
          });
        }

        function validateContactForm(form, d) {
          let firstInvalid = null;
          const markInvalid = (input, msg) => {
            input?.classList.add("is-invalid");
            if (!firstInvalid) firstInvalid = { input, msg };
          };

          form.querySelectorAll(".is-invalid").forEach((el) => el.classList.remove("is-invalid"));

          const vornameInp = form.querySelector('input[name="vorname"]');
          if (vornameInp && !vornameInp.value.trim()) {
            markInvalid(vornameInp, "Bitte gib deinen Vornamen ein.");
          }

          const nachnameInp = form.querySelector('input[name="nachname"]');
          if (nachnameInp && !nachnameInp.value.trim()) {
            markInvalid(nachnameInp, "Bitte gib deinen Nachnamen ein.");
          }

          const emailInp = form.querySelector('input[name="email"]');
          if (emailInp) {
            const val = emailInp.value.trim();
            const isRequired = d.mail === "1" || d.mail === "true" || d.mail === true;
            if (isRequired && !val) {
              markInvalid(emailInp, "Bitte gib deine E-Mail-Adresse ein.");
            } else if (val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
              markInvalid(emailInp, "Bitte gib eine gültige E-Mail-Adresse ein (z. B. name@beispiel.de).");
            }
          }

          const telInp = form.querySelector('input[name="telefon"]');
          if (telInp) {
            const val = telInp.value.trim();
            const isRequired = d.phone === "1" || d.phone === "true" || d.phone === true;
            if (isRequired && !val) {
              markInvalid(telInp, "Bitte gib deine Telefonnummer ein.");
            } else if (val && !/^[0-9+\s()/-]{6,}$/.test(val)) {
              markInvalid(telInp, "Bitte gib eine gültige Telefonnummer ein (mind. 6 Ziffern).");
            }
          }

          if (d.address === "1") {
            const strasse = form.querySelector('input[name="strasse"]');
            const plz = form.querySelector('input[name="plz"]');
            const ort = form.querySelector('input[name="ort"]');
            if (strasse && !strasse.value.trim()) markInvalid(strasse, "Bitte gib deine Straße & Hausnummer ein.");
            if (plz && !plz.value.trim()) markInvalid(plz, "Bitte gib deine Postleitzahl ein.");
            if (ort && !ort.value.trim()) markInvalid(ort, "Bitte gib deinen Wohnort ein.");
          }

          const isConditionsRequired =
            d.conditions === "1" || d.conditions === "true" || d.conditions === true;
          if (isConditionsRequired) {
            const condCb = form.querySelector('input[name="conditions"]');
            if (condCb && !condCb.checked) {
              markInvalid(condCb, "Bitte akzeptiere die Teilnahmebedingungen.");
            }
          }

          const isDatenschutzRequired =
            d.datenschutz === "1" || d.datenschutz === "true" || d.datenschutz === true;
          if (isDatenschutzRequired) {
            const dsCb = form.querySelector('input[name="datenschutz"]');
            if (dsCb && !dsCb.checked) {
              markInvalid(dsCb, "Bitte akzeptiere die Datenschutzerklärung.");
            }
          }

          return firstInvalid;
        }

        const kontaktForm = container.querySelector(".kontakt-form");
        if (kontaktForm) {
          kontaktForm.querySelectorAll("input, select").forEach((inp) => {
            const clearField = () => {
              inp.classList.remove("is-invalid");
              if (!kontaktForm.querySelector(".is-invalid")) {
                clearInlineError();
              }
            };
            inp.addEventListener("input", clearField);
            inp.addEventListener("change", clearField);
          });

          kontaktForm.addEventListener("submit", (e) => {
            e.preventDefault();
            const invalid = validateContactForm(kontaktForm, node.data || {});
            if (invalid) {
              showInlineError(invalid.msg);
              invalid.input?.focus();
              return;
            }
            clearInlineError();
            const data = {};
            new FormData(kontaktForm).forEach((v, k) => { data[k] = v; });
            goNext(node, 1, data);
          });
        }
      }

        function calculateTotalSteps(startNodeId) {
          function getMaxDepth(nodeId, visited = new Set()) {
            if (!nodeId || visited.has(nodeId) || !nodes[nodeId]) return 0;
            visited.add(nodeId);
            const n = nodes[nodeId];
            if (n.class === "save" || n.class === "link") return 0;
            const isLogic = n.class === "weiche" || n.class === "condition";
            let maxChild = 0;
            const outputs = n.outputs || {};
            Object.values(outputs).forEach((out) => {
              (out.connections || []).forEach((c) => {
                const childDepth = getMaxDepth(String(c.node), new Set(visited));
                if (childDepth > maxChild) maxChild = childDepth;
              });
            });
            return (isLogic ? 0 : 1) + maxChild;
          }
          return getMaxDepth(startNodeId);
        }

        function createProgressElement(node) {
          const visibleHistoryCount = history.filter(
            (id) => nodes[id] && nodes[id].class !== "weiche" && nodes[id].class !== "condition"
          ).length;
          const currentStep = visibleHistoryCount + 1;
          const entryId = entry ? String(entry.id) : String(node.id);
          const maxPath = calculateTotalSteps(entryId) || 1;
          const totalSteps = Math.max(currentStep, maxPath);

          if (totalSteps < 2) return null;

          const percent = Math.min(100, Math.round((currentStep / totalSteps) * 100));

          const wrap = document.createElement("div");
          wrap.className = "survey-progress-wrap";
          wrap.innerHTML =
            '<div class="survey-progress-info">' +
            '<span class="survey-step-count"><i class="bi bi-flag-fill"></i> Frage ' +
            currentStep +
            " von " +
            totalSteps +
            "</span>" +
            '<span class="survey-progress-percent">' +
            percent +
            "%</span>" +
            "</div>" +
            '<div class="survey-progress-bar">' +
            '<div class="survey-progress-fill" style="width: ' +
            percent +
            '%;"></div>' +
            "</div>";
          return wrap;
        }

        function renderStep(node) {
          let frag;
          switch (node.class) {
            case "info": frag = renderInfo(node); break;
            case "freitext": frag = renderFreitext(node); break;
            case "einfach": frag = renderEinfach(node); break;
            case "mehrfach": frag = renderMehrfach(node); break;
            case "skala": frag = renderSkala(node); break;
            case "slider": frag = renderSlider(node); break;
            case "weiche":
            case "condition": {
              let prevId = null;
              for (let i = history.length - 1; i >= 0; i--) {
                const hid = history[i];
                if (nodes[hid] && nodes[hid].class !== "weiche" && nodes[hid].class !== "condition") {
                  prevId = hid;
                  break;
                }
              }
              const prevAnswer = prevId ? answers[prevId]?.answer : null;
              const op = node.data?.operator || "gte";
              const targetVal = node.data?.value !== undefined ? node.data.value : "4";
              const isMatch = evaluateCondition(prevAnswer, op, targetVal);
              const outputIndex = isMatch ? 1 : 2;

              history.push(String(node.id));
              const next = getNextNode(node, outputIndex);
              if (next) {
                renderStep(next);
              } else {
                handleTerminal(node);
              }
              return;
            }
            case "frage2":
            case "frage3":
            case "frage4": frag = renderFrage(node); break;
            case "kontakt": frag = renderKontakt(node); break;
            case "save": showThankYou(node); return;
            case "link":
              if (node.data.url) {
                const url = node.data.url;
                window.open(url.startsWith("http") ? url : "https://" + url, "_blank");
              }
              return;
            default: return;
          }
          const card = frag.querySelector(".survey-card");
          if (card && node.class !== "save" && node.class !== "link") {
            const progressEl = createProgressElement(node);
            if (progressEl) {
              const contentCol = card.querySelector(".survey-split-content");
              if (contentCol) {
                contentCol.prepend(progressEl);
              } else {
                card.prepend(progressEl);
              }
            }
          }
          if (card && card.classList.contains("survey-card-split")) {
            container.classList.add("has-split-card");
          } else {
            container.classList.remove("has-split-card");
          }
          container.replaceChildren(frag);
          bindEvents(node);
        }

      function findEntryNode() {
        const allNodes = Object.values(nodes);
        const targetIds = new Set();
        allNodes.forEach((n) => {
          Object.values(n.outputs || {}).forEach((out) => {
            (out.connections || []).forEach((c) => targetIds.add(String(c.node)));
          });
        });
        return allNodes.find((n) => !targetIds.has(String(n.id))) || allNodes[0];
      }

      const entry = findEntryNode();
      if (entry) {
        renderStep(entry);
      } else {
        container.innerHTML = '<div class="alert alert-info text-center">Keine Umfrage-Knoten gefunden.</div>';
      }
    })();
  </script>
</body>
</html>`;
    }

    function openPreview() {
      syncDomToNodeData();
      const exportData = editor.export();
      const previewHtml = buildPreviewHtml(exportData);
      previewIframe.srcdoc = previewHtml;

      if (typeof bootstrap !== "undefined" && bootstrap.Modal) {
        bsPreviewModal = bootstrap.Modal.getOrCreateInstance(previewModalEl);
        bsPreviewModal.show();
      } else {
        previewModalEl.classList.add("show");
        previewModalEl.style.display = "block";
      }
    }

    previewBtn.addEventListener("click", () => {
      validateSurveyStructure();
      openPreview();
    });

    if (restartBtn) {
      restartBtn.addEventListener("click", () => {
        openPreview();
      });
    }

    if (frameContainer) {
      const setDevice = (device) => {
        [mobileBtn, tabletBtn, desktopBtn].forEach((b) => b?.classList.remove("active"));
        if (device === "mobile") {
          mobileBtn?.classList.add("active");
          frameContainer.className = "preview-mobile";
        } else if (device === "tablet") {
          tabletBtn?.classList.add("active");
          frameContainer.className = "preview-tablet";
        } else {
          desktopBtn?.classList.add("active");
          frameContainer.className = "preview-desktop";
        }
      };

      if (mobileBtn) mobileBtn.addEventListener("click", () => setDevice("mobile"));
      if (tabletBtn) tabletBtn.addEventListener("click", () => setDevice("tablet"));
      if (desktopBtn) desktopBtn.addEventListener("click", () => setDevice("desktop"));
    }
  }

  // --- Template Loader ---

  function setupTemplateLoader() {
    const templateBtns = document.querySelectorAll(".survey-template-btn");
    templateBtns.forEach((btn) => {
      btn.addEventListener("click", async (e) => {
        e.preventDefault();
        const templateKey = btn.getAttribute("data-template");
        const templateData = surveyTemplates[templateKey];
        if (!templateData || !editor) return;

        const currentData = editor.export();
        const hasNodes = Object.keys(currentData?.drawflow?.Home?.data || {}).length > 0;
        const hasResults = document.getElementById("survey-has-results")?.value === "true";

        const notifyConfirmFn = window.cs?.notifyConfirm || notifyConfirm;
        if (hasResults) {
          const confirmed = await notifyConfirmFn(
            "Achtung: Für diese Kampagne existieren bereits Antworten. Wenn du eine Vorlage lädst und speicherst, wird die bisherige Struktur ersetzt. Fortfahren?",
            "Vorlage laden",
          );
          if (confirmed) {
            loadTemplateData(templateData);
          }
        } else if (hasNodes) {
          const confirmed = await notifyConfirmFn(
            "Möchtest du die Vorlage laden? Die aktuelle Struktur im Editor wird überschrieben.",
            "Vorlage laden",
          );
          if (confirmed) {
            loadTemplateData(templateData);
          }
        } else {
          loadTemplateData(templateData);
        }
      });
    });
  }

  function loadTemplateData(templateData) {
    editor.clear();

    if (templateData.nodes) {
      const idMap = {};

      templateData.nodes.forEach((node) => {
        let inputs = 1;
        let outputs = 1;
        let html = "";

        switch (node.name) {
          case "info":
            html = structureInfo(node.id);
            break;
          case "freitext":
            html = structureFreetext(node.id);
            break;
          case "einfach":
            html = structureSingle(node.id);
            break;
          case "mehrfach":
            html = structureMulti(node.id);
            break;
          case "skala":
            html = structureScale(node.id);
            break;
          case "frage2":
            outputs = 2;
            html = structureQuestions(node.id, 2);
            break;
          case "frage3":
            outputs = 3;
            html = structureQuestions(node.id, 3);
            break;
          case "frage4":
            outputs = 4;
            html = structureQuestions(node.id, 4);
            break;
          case "kontakt":
            html = structureContact(node.id);
            break;
          case "link":
            outputs = 0;
            html = structureRedirect(node.id);
            break;
          case "slider":
            inputs = 1;
            outputs = 1;
            html = structureSlider(node.id);
            break;
          case "weiche":
          case "condition":
            inputs = 1;
            outputs = 2;
            html = structureCondition(node.id);
            break;
          case "save":
            outputs = 0;
            html = structureSave(node.id);
            break;
        }

        const actualId = editor.addNode(
          node.name,
          inputs,
          outputs,
          node.pos_x,
          node.pos_y,
          node.class,
          node.data || {},
          html,
        );

        idMap[node.id] = actualId;

        const nodeEl = drawflowEl.querySelector(`#node-${actualId}`);
        if (nodeEl && node.data) {
          Object.entries(node.data).forEach(([key, val]) => {
            const input = nodeEl.querySelector(`[df-${key}]`);
            if (input) {
              if (input.type === "checkbox") {
                input.checked = Boolean(val);
              } else {
                input.value = val;
              }
            }
          });
        }
      });

      if (templateData.connections) {
        templateData.connections.forEach(([fromId, toId, fromOutput, toInput]) => {
          const realFrom = idMap[fromId];
          const realTo = idMap[toId];
          if (realFrom && realTo) {
            editor.addConnection(realFrom, realTo, fromOutput || "output_1", toInput || "input_1");
          }
        });
      }
    } else if (templateData.drawflow) {
      const cloned = JSON.parse(JSON.stringify(templateData));
      editor.import(injectHtml(cloned));
    }

    if (templateData.style) {
      currentSurveyStyle = { ...DEFAULT_SURVEY_STYLE, ...templateData.style, kioskTimeout: 0 };
      populateStyleModal(currentSurveyStyle);
    }

    bindScaleTypeEvents();
    bindPhoneToggleEvents();
    bindConditionsToggleEvents();
    bindNotificationChannelToggleEvents();
    bindDatenschutzToggleEvents();
    unsavedChanges = true;
    validateSurveyStructure();
    fitToScreen();
    pushState(true);

    const notifySuccessFn = window.cs?.notifySuccess || notifySuccess;
    notifySuccessFn("Umfrage-Vorlage erfolgreich geladen!", "Vorlage geladen");
  }

  // --- Drag & Drop ---

  function setupDragAndDrop() {
    const elements = document.querySelectorAll(".drag-drawflow");

    elements.forEach((el) => {
      el.addEventListener("dragstart", (ev) => {
        ev.dataTransfer.setData("node", el.getAttribute("data-node"));
      });

      // Touch support
      el.addEventListener(
        "touchstart",
        (ev) => {
          el._touchNode = el.getAttribute("data-node");
        },
        false,
      );

      el.addEventListener(
        "touchmove",
        (ev) => {
          el._lastTouch = ev;
        },
        false,
      );

      el.addEventListener(
        "touchend",
        (ev) => {
          if (el._lastTouch) {
            const touch = el._lastTouch.touches[0];
            const target = document.elementFromPoint(touch.clientX, touch.clientY);
            if (target && target.closest("#drawflow")) {
              addNodeToDrawFlow(el._touchNode, touch.clientX, touch.clientY);
            }
          }
          el._touchNode = "";
          el._lastTouch = null;
        },
        false,
      );
    });

    drawflowEl.addEventListener("drop", (ev) => {
      ev.preventDefault();
      const data = ev.dataTransfer.getData("node");
      addNodeToDrawFlow(data, ev.clientX, ev.clientY);
    });

    drawflowEl.addEventListener("dragover", (ev) => {
      ev.preventDefault();
    });
  }

  // --- Add Node ---

  function addNodeToDrawFlow(name, posX, posY) {
    if (editor.editor_mode === "fixed") return;

    posX =
      posX * (editor.precanvas.clientWidth / (editor.precanvas.clientWidth * editor.zoom)) -
      editor.precanvas.getBoundingClientRect().x *
        (editor.precanvas.clientWidth / (editor.precanvas.clientWidth * editor.zoom));
    posY =
      posY * (editor.precanvas.clientHeight / (editor.precanvas.clientHeight * editor.zoom)) -
      editor.precanvas.getBoundingClientRect().y *
        (editor.precanvas.clientHeight / (editor.precanvas.clientHeight * editor.zoom));

    const nodeCount = editor.nodeId;

    switch (name) {
      case "frage2":
        editor.addNode("frage2", 1, 2, posX, posY, "frage2", {}, structureQuestions(nodeCount, 2));
        break;
      case "frage3":
        editor.addNode("frage3", 1, 3, posX, posY, "frage3", {}, structureQuestions(nodeCount, 3));
        break;
      case "frage4":
        editor.addNode("frage4", 1, 4, posX, posY, "frage4", {}, structureQuestions(nodeCount, 4));
        break;
      case "einfach":
        editor.addNode("einfach", 1, 1, posX, posY, "einfach", {}, structureSingle(nodeCount));
        break;
      case "mehrfach":
        editor.addNode("mehrfach", 1, 1, posX, posY, "mehrfach", {}, structureMulti(nodeCount));
        break;
      case "freitext":
        editor.addNode("freitext", 1, 1, posX, posY, "freitext", {}, structureFreetext(nodeCount));
        break;
      case "info":
        editor.addNode("info", 1, 1, posX, posY, "info", {}, structureInfo(nodeCount));
        break;
      case "skala":
        editor.addNode(
          "skala",
          1,
          1,
          posX,
          posY,
          "skala",
          { type: "star", count: "5" },
          structureScale(nodeCount),
        );
        break;
      case "slider":
        editor.addNode(
          "slider",
          1,
          1,
          posX,
          posY,
          "slider",
          { min: "0", max: "100", step: "1", default: "50", unit: "€", prefix: "" },
          structureSlider(nodeCount),
        );
        break;
      case "weiche":
        editor.addNode(
          "weiche",
          1,
          2,
          posX,
          posY,
          "weiche",
          { operator: "gte", value: "4" },
          structureCondition(nodeCount),
        );
        break;
      case "link":
        editor.addNode("link", 1, 0, posX, posY, "link", {}, structureRedirect(nodeCount));
        break;
      case "kontakt":
        editor.addNode(
          "kontakt",
          1,
          1,
          posX,
          posY,
          "kontakt",
          { mail: "1", phone: "optional" },
          structureContact(nodeCount),
        );
        break;
      case "save":
        editor.addNode("save", 1, 0, posX, posY, "save", {}, structureSave(nodeCount));
        break;
    }

    validateSurveyStructure();
  }

  // --- Undo / Redo System ---

  const undoStack = [];
  const redoStack = [];
  const MAX_HISTORY = 40;
  let isUndoRedoAction = false;
  let snapshotTimer = null;

  function getSnapshot() {
    if (!editor) return null;
    try {
      return JSON.stringify(editor.export());
    } catch {
      return null;
    }
  }

  function pushState(immediate = false) {
    if (!editor || isUndoRedoAction || editor.editor_mode === "fixed") return;

    if (immediate) {
      if (snapshotTimer) {
        clearTimeout(snapshotTimer);
        snapshotTimer = null;
      }
      doPushState();
    } else {
      if (snapshotTimer) clearTimeout(snapshotTimer);
      snapshotTimer = setTimeout(doPushState, 200);
    }
  }

  function doPushState() {
    snapshotTimer = null;
    if (!editor || isUndoRedoAction) return;

    const currentJson = getSnapshot();
    if (!currentJson) return;

    if (undoStack.length > 0 && undoStack[undoStack.length - 1] === currentJson) {
      return;
    }

    undoStack.push(currentJson);
    if (undoStack.length > MAX_HISTORY) {
      undoStack.shift();
    }
    redoStack.length = 0;
    updateUndoRedoButtons();
  }

  function restoreState(jsonStr) {
    if (!editor || !jsonStr) return;
    try {
      isUndoRedoAction = true;
      const parsed = JSON.parse(jsonStr);
      editor.import(injectHtml(parsed));
      bindScaleTypeEvents();
      bindPhoneToggleEvents();
      bindConditionsToggleEvents();
      bindNotificationChannelToggleEvents();
      bindDatenschutzToggleEvents();
      validateSurveyStructure();
      unsavedChanges = true;
    } catch (err) {
      console.error("Fehler beim Wiederherstellen des Zustands:", err);
    } finally {
      isUndoRedoAction = false;
      updateUndoRedoButtons();
    }
  }

  function undo() {
    if (editor.editor_mode === "fixed" || undoStack.length <= 1) return;

    if (snapshotTimer) {
      clearTimeout(snapshotTimer);
      snapshotTimer = null;
    }

    const current = undoStack.pop();
    redoStack.push(current);

    const prev = undoStack[undoStack.length - 1];
    restoreState(prev);
  }

  function redo() {
    if (editor.editor_mode === "fixed" || redoStack.length === 0) return;

    if (snapshotTimer) {
      clearTimeout(snapshotTimer);
      snapshotTimer = null;
    }

    const next = redoStack.pop();
    undoStack.push(next);

    restoreState(next);
  }

  function updateUndoRedoButtons() {
    const undoBtn = document.getElementById("survey-undo-btn");
    const redoBtn = document.getElementById("survey-redo-btn");

    const isFixed = editor && editor.editor_mode === "fixed";
    if (undoBtn) {
      undoBtn.disabled = isFixed || undoStack.length <= 1;
    }
    if (redoBtn) {
      redoBtn.disabled = isFixed || redoStack.length === 0;
    }
  }

  function setupUndoRedoControls() {
    const undoBtn = document.getElementById("survey-undo-btn");
    const redoBtn = document.getElementById("survey-redo-btn");

    undoBtn?.addEventListener("click", (e) => {
      e.preventDefault();
      undo();
    });

    redoBtn?.addEventListener("click", (e) => {
      e.preventDefault();
      redo();
    });

    document.addEventListener("keydown", (e) => {
      const isSurveyVisible = drawflowEl && drawflowEl.offsetParent !== null;
      if (!isSurveyVisible || (editor && editor.editor_mode === "fixed")) return;

      const activeEl = document.activeElement;
      const isEditingText =
        activeEl &&
        (activeEl.tagName === "INPUT" ||
          activeEl.tagName === "TEXTAREA" ||
          activeEl.isContentEditable);

      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const isCmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;

      if (!isCmdOrCtrl) return;

      const key = e.key.toLowerCase();

      if (key === "z") {
        if (e.shiftKey) {
          if (!isEditingText) {
            e.preventDefault();
            redo();
          }
        } else {
          if (!isEditingText) {
            e.preventDefault();
            undo();
          }
        }
      } else if (key === "y") {
        if (!isEditingText) {
          e.preventDefault();
          redo();
        }
      }
    });
  }

  // --- Node Events ---

  function syncDomToNodeData() {
    if (!editor) return;
    const allNodes = editor.drawflow?.drawflow?.[editor.module]?.data || {};
    Object.keys(allNodes).forEach((nodeId) => {
      const nodeEl = drawflowEl.querySelector(`#node-${nodeId}`);
      if (!nodeEl) return;
      const nodeObj = allNodes[nodeId];
      if (!nodeObj.data) nodeObj.data = {};
      nodeEl.querySelectorAll("input, select, textarea").forEach((el) => {
        for (let attr of el.attributes) {
          if (attr.name.startsWith("df-")) {
            const key = attr.name.slice(3);
            nodeObj.data[key] = el.type === "checkbox" ? el.checked : el.value;
          }
        }
      });
    });
  }

  function setupNodeEvents() {
    drawflowEl.addEventListener("mousedown", (e) => {
      if (e.target.closest(".btn-node-delete, .btn-options-toggle")) {
        e.stopPropagation();
      }
    });

    drawflowEl.addEventListener("change", (e) => {
      const target = e.target;
      if (!target) return;
      target.dispatchEvent(new Event("input", { bubbles: true }));
      unsavedChanges = true;
    });

    drawflowEl.addEventListener("click", (e) => {
      const optBtn = e.target.closest(".btn-options-toggle");
      if (optBtn) {
        e.preventDefault();
        e.stopPropagation();
        const contentNode = optBtn.closest(".drawflow_content_node") || optBtn.parentElement;
        const optionsDiv = contentNode?.querySelector(".options");
        if (optionsDiv) {
          optionsDiv.classList.toggle("show");
        }
        return;
      }

      const deleteBtn = e.target.closest(".btn-node-delete");
      if (!deleteBtn) return;
      e.preventDefault();
      e.stopPropagation();

      if (editor.editor_mode === "fixed") return;

      const nodeEl = deleteBtn.closest(".drawflow-node");
      if (!nodeEl) return;

      editor.removeNodeId(nodeEl.id);
      unsavedChanges = true;
      validateSurveyStructure();
    });

    editor.on("nodeCreated", () => {
      unsavedChanges = true;
      bindScaleTypeEvents();
      bindPhoneToggleEvents();
      bindConditionsToggleEvents();
      bindNotificationChannelToggleEvents();
      bindDatenschutzToggleEvents();
      validateSurveyStructure();
      pushState(true);
    });
    editor.on("nodeRemoved", () => {
      unsavedChanges = true;
      validateSurveyStructure();
      pushState(true);
    });
    editor.on("nodeMoved", () => {
      unsavedChanges = true;
      pushState(false);
    });
    editor.on("connectionCreated", () => {
      unsavedChanges = true;
      validateSurveyStructure();
      pushState(true);
    });
    editor.on("connectionRemoved", () => {
      unsavedChanges = true;
      validateSurveyStructure();
      pushState(true);
    });
    editor.on("nodeDataChanged", () => {
      unsavedChanges = true;
      validateSurveyStructure();
      pushState(false);
    });
  }

  function setupUnloadWarning() {
    window.addEventListener("beforeunload", (e) => {
      if (unsavedChanges) {
        e.preventDefault();
      }
    });
  }

  function bindScaleTypeEvents() {
    drawflowEl.querySelectorAll(".scale-type").forEach((select) => {
      if (select._bound) return;
      select._bound = true;
      select.addEventListener("change", () => toggleScaleField(select));
      toggleScaleField(select);
    });
  }

  function toggleScaleField(select) {
    const hiddenRow = select.closest(".answers, .options")?.querySelector(".hidden-row");
    if (!hiddenRow) return;

    if (select.value === "smiley3" || select.value === "smiley5") {
      hiddenRow.style.display = "none";
    } else {
      hiddenRow.style.display = "";
    }
  }

  function bindPhoneToggleEvents() {
    drawflowEl.querySelectorAll(".phone-toggle").forEach((select) => {
      if (select._bound) return;
      select._bound = true;
      select.addEventListener("change", () => toggleChatbot(select));
      toggleChatbot(select);
    });
  }

  function toggleChatbot(select) {
    const parentNode = select.closest(".drawflow_content_node") || select.closest(".drawflow-node");
    const chatbotRow = parentNode?.querySelector(".chatbot-row");
    if (!chatbotRow) return;
    chatbotRow.style.display = select.value === "0" ? "none" : "";
  }

  function bindConditionsToggleEvents() {
    drawflowEl.querySelectorAll(".conditions-toggle").forEach((select) => {
      if (select._conditionsBound) return;
      select._conditionsBound = true;
      select.addEventListener("change", () => toggleConditionsText(select));
      toggleConditionsText(select);
    });
  }

  function toggleConditionsText(select) {
    const parentNode = select.closest(".drawflow_content_node") || select.closest(".drawflow-node");
    const textRow = parentNode?.querySelector(".conditions-text-row");
    if (!textRow) return;
    textRow.style.display = select.value === "0" ? "none" : "";
  }

  function bindDatenschutzToggleEvents() {
    drawflowEl.querySelectorAll(".datenschutz-toggle").forEach((select) => {
      if (select._datenschutzBound) return;
      select._datenschutzBound = true;
      select.addEventListener("change", () => toggleDatenschutzUrl(select));
      toggleDatenschutzUrl(select);
    });
  }

  function toggleDatenschutzUrl(select) {
    const parentNode = select.closest(".drawflow_content_node") || select.closest(".drawflow-node");
    const urlRow = parentNode?.querySelector(".datenschutz-url-row");
    if (!urlRow) return;
    urlRow.style.display = select.value === "0" ? "none" : "";
  }

  function bindNotificationChannelToggleEvents() {
    drawflowEl.querySelectorAll(".channel-toggle").forEach((select) => {
      if (select._channelBound) return;
      select._channelBound = true;
      select.addEventListener("change", () => toggleNotificationTemplates(select));
      // Optionally trigger once on load to populate
      if (select.value) {
        toggleNotificationTemplates(select);
      }
    });
  }

  function toggleNotificationTemplates(select) {
    const parentNode = select.closest(".drawflow-node");
    const templateRow = parentNode?.querySelector(".template-row");
    const templateSelect = parentNode?.querySelector(".template-select");
    if (!templateRow || !templateSelect) return;

    if (!select.value) {
      templateRow.style.display = "none";
      templateSelect.innerHTML = '<option value="">-- Keine Vorlage --</option>';
      return;
    }

    templateRow.style.display = "";
    templateSelect.innerHTML = '<option value="">Lade Vorlagen...</option>';

    fetch(`/api/cloud-messaging/templates?channel=${select.value}&type=KAMPAGNE`)
      .then((res) => res.json())
      .then((templates) => {
        templateSelect.innerHTML = '<option value="">-- Keine Vorlage --</option>';
        if (templates && templates.length > 0) {
          templates.forEach((t) => {
            let option = document.createElement("option");
            option.value = t.id;
            option.textContent = t.beschreibung || t.subject;
            templateSelect.appendChild(option);
          });
          // Restore selected value if drawflow node has data
          const nodeId = parentNode.id.replace("node-", "");
          const nodeData = editor.drawflow?.drawflow?.Home?.data?.[nodeId]?.data;
          if (nodeData && nodeData.notificationtemplateid) {
            templateSelect.value = nodeData.notificationtemplateid;
          }
        }
      })
      .catch((err) => {
        console.error("Fehler beim Laden der Vorlagen", err);
        templateSelect.innerHTML = '<option value="">-- Fehler --</option>';
      });
  }

  // --- Save ---

  function setupSaveButton() {
    const saveBtn = document.getElementById("survey-save-btn");
    const campaignId = document.getElementById("survey-campaign-id")?.value;
    if (!saveBtn || !campaignId) return;

    saveBtn.addEventListener("click", async () => {
      syncDomToNodeData();
      const validation = validateSurveyStructure();
      if (!validation.valid && validation.warnings.length > 0) {
        const notifyConfirmFn = window.cs?.notifyConfirm || notifyConfirm;
        const confirmed = await notifyConfirmFn(
          `Die Umfrage enthält ${validation.warnings.length} Warnung(en) (z. B. unverbundene Knoten). Möchtest du trotzdem speichern?`,
          "Struktur prüfen",
        );
        if (confirmed) {
          executeSave();
        }
      } else {
        executeSave();
      }
    });

    async function executeSave() {
      syncDomToNodeData();
      const exportObj = editor.export();
      exportObj.style = { ...currentSurveyStyle };
      const exportData = JSON.stringify(exportObj);
      const csrfMeta = document.querySelector('meta[name="_csrf"]');
      const csrfHeaderMeta = document.querySelector('meta[name="_csrf_header"]');
      const headers = { "Content-Type": "application/json" };

      if (csrfMeta && csrfHeaderMeta) {
        headers[csrfHeaderMeta.content] = csrfMeta.content;
      }

      saveBtn.disabled = true;
      try {
        const response = await fetch(`/campaigns/${campaignId}/save-survey-structure`, {
          method: "POST",
          headers,
          body: exportData,
        });

        if (response.ok) {
          unsavedChanges = false;
          const notifyFn = window.cs?.notifySuccess || notifySuccess;
          notifyFn("Umfrage-Struktur erfolgreich gespeichert!", "Speichern");
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      } catch (err) {
        console.error("Fehler beim Speichern der Umfrage-Struktur:", err);
        const notifyErrFn = window.cs?.notifyError || notifyError;
        notifyErrFn("Umfrage-Struktur konnte nicht gespeichert werden.", "Fehler");
      } finally {
        saveBtn.disabled = false;
      }
    }
  }

  // --- Import ---

  function injectHtml(data) {
    const nodes = data?.drawflow?.Home?.data ?? {};
    Object.values(nodes).forEach((node) => {
      const id = node.id;
      if (
        node.class === "save" ||
        node.class === "link" ||
        node.name === "save" ||
        node.name === "link"
      ) {
        node.outputs = {};
      }
      switch (node.class) {
        case "info":
          node.html = structureInfo(id);
          break;
        case "freitext":
          node.html = structureFreetext(id);
          break;
        case "einfach":
          node.html = structureSingle(id);
          break;
        case "mehrfach":
          node.html = structureMulti(id);
          break;
        case "skala":
          node.html = structureScale(id);
          break;
        case "slider":
          node.html = structureSlider(id);
          break;
        case "weiche":
        case "condition":
          node.html = structureCondition(id);
          break;
        case "frage2":
          node.html = structureQuestions(id, 2);
          break;
        case "frage3":
          node.html = structureQuestions(id, 3);
          break;
        case "frage4":
          node.html = structureQuestions(id, 4);
          break;
        case "kontakt":
          node.html = structureContact(id);
          break;
        case "link":
          node.html = structureRedirect(id);
          break;
        case "save":
          node.html = structureSave(id);
          break;
      }
    });
    return data;
  }

  async function importExistingStructure() {
    const campaignId = document.getElementById("survey-campaign-id")?.value;
    if (!campaignId) return;

    try {
      const response = await fetch(`/campaigns/${campaignId}/survey-structure`);
      if (!response.ok) {
        console.error("Fehler beim Laden der Umfrage-Struktur: Status", response.status);
        return;
      }

      const text = await response.text();
      const trimmed = text ? text.trim() : "";
      if (!trimmed || trimmed === "{}" || trimmed.startsWith("<")) return;

      try {
        const jsonData = JSON.parse(trimmed);
        if (jsonData && jsonData.style) {
          currentSurveyStyle = { ...DEFAULT_SURVEY_STYLE, ...jsonData.style };
          populateStyleModal(currentSurveyStyle);
        }
        editor.import(injectHtml(jsonData));
      } catch (parseErr) {
        console.error(
          "Fehler beim Parsen der Umfrage-Struktur als JSON:",
          parseErr,
          trimmed.substring(0, 50),
        );
        return;
      }

      bindScaleTypeEvents();
      bindPhoneToggleEvents();
      bindConditionsToggleEvents();
      bindNotificationChannelToggleEvents();
      bindDatenschutzToggleEvents();
      validateSurveyStructure();
      pushState(true);
    } catch (err) {
      console.error("Fehler beim Importieren der Umfrage-Struktur:", err);
    } finally {
      if (undoStack.length === 0) {
        pushState(true);
      }
    }
  }
});
